import MediaImage from './MediaImage';
import PlacePresentationEditor from './PlacePresentationEditor.js';
import { normalizeShapeTypeName, getShapeBounds, getBoxShapePoints,
  isRectangularOverviewShape, getOverviewShapePoints, getOverviewRenderLayer,
  getOverviewColor, getPointBounds, collectOverviewContextPlaces, getOverviewContextBuffer, getMultipartPathData } from './placeOverviewMap.js';
import EntityBiomeFields from './EntityBiomeFields.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyGeneratedRouteGeometry, isRoadPathDraft } from './placeRouteDraft.js';
import NaturalPathEditor from './NaturalPathEditor.js';

export { getOverviewColor, collectOverviewContextPlaces } from './placeOverviewMap.js';

const NUMERIC_FIELDS = new Set([
  'createDateYear',
  'createDateDay',
  'destroyDateYear',
  'destroyDateDay',
  'level',
]);

const LOCATION_NUMERIC_FIELDS = new Set(['x', 'y', 'z', 'maxRadius']);

const SHAPE_NUMERIC_FIELDS = new Set(['minX', 'maxX', 'minY', 'maxY', 'minZ', 'maxZ']);

const CURRENCY_NUMERIC_FIELDS = new Set(['idCurrency', 'quantity']);

const SHAPE_TYPES = ['Point', 'Sphere', 'Cylinder', 'Box', 'Polygon', 'Path', 'Polyhedron', 'Heightfield'];
const PLACE_OVERVIEW_SIZE = 720;
const FEET_PER_MILE = 5280;
const PLACE_OVERVIEW_MIN_ZOOM = 0.25;
const PLACE_OVERVIEW_ZOOM_STEP = 1.35;
const PLACE_OVERVIEW_SNAPSHOT_SIZE = 1024;
const SparklesIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2zm7 12l.9 2.1L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.9L19 14zM5 14l1.1 2.9L9 18l-2.9 1.1L5 22l-1.1-2.9L1 18l2.9-1.1L5 14z"
      fill="currentColor"
    />
  </svg>
);

const SaveIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path d="M5 3h11l3 3v15H5V3zm2 2v4h8V5H7zm0 14h10v-8H7v8zm2-6h6v4H9v-4z" fill="currentColor" />
  </svg>
);

const CopyIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path d="M9 9h10v12H9V9zm-4-6h10v2H7v10H5V3zm4 2h8v2H9V5zm2 8v2h6v-2h-6zm0 4v2h6v-2h-6z" fill="currentColor" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="button-icon">
    <path
      d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9zm1 11h8a2 2 0 0 0 2-2V8H6v10a2 2 0 0 0 2 2z"
      fill="currentColor"
    />
  </svg>
);

const createEmptyPlace = () => ({
  idEntity: '',
  name: '',
  title: '',
  description: '',
  entityType: 'Place',
  createDateAge: '',
  createDateYear: '',
  createDateSeason: '',
  createDateDay: '',
  destroyDateAge: '',
  destroyDateYear: '',
  destroyDateSeason: '',
  destroyDateDay: '',
  level: '',
  placeType: '',
  zoneType: '',
  buildingType: '',
  transitPointType: '',
  location: { x: 0, y: 0, z: 0, maxRadius: 0 },
  shape: {
    shapeType: 'Box',
    minX: 0, maxX: 0,
    minY: 0, maxY: 0,
    minZ: 0, maxZ: 0,
    shapeData: {},
  },
  composedOfParents: [],
  composedOfChildren: [],
  containedItems: [],
  currencies: [],
  savedInhabitants: [],
  childPlaceSuggestions: [],
  inhabitantSuggestions: [],
  itemSuggestions: [],
  adjacentPlaceSuggestions: [],
  siblingPlaces: [],
  adjacentPlaces: [],
});

const getPlaceLabel = (place) =>
  place?.name || place?.title || place?.entityName || place?.entityTitle || place?.idEntity || 'Unnamed place';

const loadSnapshotImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to render the top-down overview snapshot.'));
    image.src = url;
  });

const renderOverviewSvgSnapshot = async (svgElement, overviewPlan) => {
  if (!svgElement || !overviewPlan) {
    throw new Error('Top-down overview is not available.');
  }

  const clone = svgElement.cloneNode(true);
  clone.setAttribute('width', String(overviewPlan.width));
  clone.setAttribute('height', String(overviewPlan.height));
  clone.setAttribute('viewBox', `0 0 ${overviewPlan.width} ${overviewPlan.height}`);

  const backgroundRect = clone.querySelector('rect[fill="#f8fbff"]');
  if (backgroundRect) {
    backgroundRect.setAttribute('x', '0');
    backgroundRect.setAttribute('y', '0');
    backgroundRect.setAttribute('width', String(overviewPlan.width));
    backgroundRect.setAttribute('height', String(overviewPlan.height));
  }

  clone
    .querySelectorAll('circle[fill="#dc2626"], circle[fill="transparent"], line[stroke="rgba(0, 0, 0, 0.001)"]')
    .forEach((element) => element.remove());

  const svgText = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await loadSnapshotImage(objectUrl);
    const canvas = document.createElement('canvas');
    canvas.width = PLACE_OVERVIEW_SNAPSHOT_SIZE;
    canvas.height = PLACE_OVERVIEW_SNAPSHOT_SIZE;
    const context = canvas.getContext('2d');
    context.fillStyle = '#f8fbff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const imageReferenceStyle = node => node.isSelected
  ? { fill: node.imageGeometryKind === 'physical-path' ? 'none' : '#fbbf24',
      stroke: node.imageGeometryKind === 'area-footprint' ? 'none' : '#f59e0b' }
  : { fill: node.fill, stroke: node.imageGeometryKind === 'area-footprint' ? 'none' : node.stroke };

export const createOverviewSnapshotSvgElement = (overviewPlan) => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(overviewPlan.width));
  svg.setAttribute('height', String(overviewPlan.height));
  svg.setAttribute('viewBox', `0 0 ${overviewPlan.width} ${overviewPlan.height}`);

  const background = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  background.setAttribute('x', '0');
  background.setAttribute('y', '0');
  background.setAttribute('width', String(overviewPlan.width));
  background.setAttribute('height', String(overviewPlan.height));
  background.setAttribute('fill', '#f8fbff');
  svg.appendChild(background);

  const setShapeAttributes = (element, node, strokeWidth) => {
    const style = imageReferenceStyle(node);
    element.setAttribute('fill', style.fill || 'none');
    element.setAttribute('stroke', style.stroke || '#475569');
    element.setAttribute('stroke-width', String(strokeWidth));
    element.setAttribute('data-place-id', node.id);
    element.setAttribute('opacity', node.isSelected ? '1' : '0.55');
    svg.appendChild(element);
  };

  overviewPlan.nodes.forEach((node) => {
    const shapeType = normalizeShapeTypeName(node.shapeType);
    const strokeWidth = shapeType === 'path'
      ? node.snapshotPathStrokeWidth
      : node.isSelected ? 2.75 : 1.5;

    if (shapeType === 'polygon' && node.points) {
      const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      polygon.setAttribute('points', node.points);
      setShapeAttributes(polygon, node, strokeWidth);
      return;
    }

    if (shapeType === 'path' && (node.pathData || node.points)) {
      const polyline = document.createElementNS('http://www.w3.org/2000/svg', node.pathData ? 'path' : 'polyline');
      polyline.setAttribute(node.pathData ? 'd' : 'points', node.pathData || node.points);
      polyline.setAttribute('fill', 'none');
      polyline.setAttribute('stroke-linecap', 'butt');
      polyline.setAttribute('stroke-linejoin', 'round');
      setShapeAttributes(polyline, { ...node, fill: 'none' }, strokeWidth);
      return;
    }

    if (['sphere', 'cylinder'].includes(shapeType)) {
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', String(node.centerX));
      circle.setAttribute('cy', String(node.centerY));
      circle.setAttribute('r', String(node.radius));
      setShapeAttributes(circle, node, strokeWidth);
      return;
    }

    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', String(node.x));
    rect.setAttribute('y', String(node.y));
    rect.setAttribute('width', String(node.width));
    rect.setAttribute('height', String(node.height));
    setShapeAttributes(rect, node, strokeWidth);
  });

  overviewPlan.nodes
    .filter((node) => node.imageGeometryKind !== 'area-footprint' && node.renderLayer > 0 && (node.points || node.pathData))
    .forEach((node) => {
      const shapeType = normalizeShapeTypeName(node.shapeType);
      if (!['path', 'polygon'].includes(shapeType)) {
        return;
      }

      const overlay = document.createElementNS(
        'http://www.w3.org/2000/svg',
        node.pathData ? 'path' : shapeType === 'path' ? 'polyline' : 'polygon'
      );
      overlay.setAttribute(node.pathData ? 'd' : 'points', node.pathData || node.points);
      overlay.setAttribute('fill', 'none');
      overlay.setAttribute('stroke', imageReferenceStyle(node).stroke || '#475569');
      overlay.setAttribute('opacity', node.isSelected ? '1' : '0.55');
      overlay.setAttribute('stroke-width', String(shapeType === 'path' ? node.snapshotPathStrokeWidth : 1.5));
      overlay.setAttribute('stroke-linecap', 'butt');
      overlay.setAttribute('stroke-linejoin', 'round');
      svg.appendChild(overlay);
    });

  return svg;
};

const renderOverviewPlanSnapshot = (overviewPlan) =>
  renderOverviewSvgSnapshot(createOverviewSnapshotSvgElement(overviewPlan), overviewPlan);

const formatMiles = (value) =>
  Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '';

const isSameEntityId = (left, right) => String(left ?? '') === String(right ?? '');

const getAxisDistanceMiles = (minValue, maxValue) => {
  const min = Number(minValue);
  const max = Number(maxValue);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  return Math.abs(max - min) / FEET_PER_MILE;
};

const getShapePointKey = (shapeData) => {
  if (Array.isArray(shapeData?.points)) return 'points';
  if (Array.isArray(shapeData?.vertices)) return 'vertices';
  return '';
};

const getEditableShapePointState = (place, pathIndex = null) => {
  const shape = place?.shape ?? {};
  const shapeType = normalizeShapeTypeName(shape.shapeType);
  const shapeData = shape.shapeData && typeof shape.shapeData === 'object' ? shape.shapeData : {};
  const pointKey = getShapePointKey(shapeData);
  const isRectangularShape = isRectangularOverviewShape(shapeType);

  if (shapeType === 'path' && Array.isArray(shapeData.paths)) {
    return {
      pointKey: 'paths',
      pathIndex,
      points: Number.isInteger(pathIndex) ? shapeData.paths[pathIndex] ?? [] : [],
      nextShapeType: shape.shapeType,
      sourceShapeType: shapeType,
    };
  }

  if (isRectangularShape) {
    return {
      pointKey: 'points',
      points: getBoxShapePoints(place),
      nextShapeType: 'Polygon',
      sourceShapeType: shapeType,
    };
  }

  if (pointKey && Array.isArray(shapeData[pointKey])) {
    return {
      pointKey,
      points: shapeData[pointKey],
      nextShapeType: shape.shapeType,
      sourceShapeType: shapeType,
    };
  }

  return {
    pointKey: '',
    points: [],
    nextShapeType: shape.shapeType,
    sourceShapeType: shapeType,
  };
};

const translateShapeData = (shapeData, deltaX, deltaZ) => {
  if (!shapeData || typeof shapeData !== 'object') {
    return shapeData ?? {};
  }

  const translatePoints = (points) =>
    Array.isArray(points)
      ? points.map((point) => ({
          ...point,
          x: Number.isFinite(Number(point?.x)) ? Number(point.x) + deltaX : point?.x,
          z: Number.isFinite(Number(point?.z)) ? Number(point.z) + deltaZ : point?.z,
        }))
      : points;

  const nextShapeData = { ...shapeData };

  if (Array.isArray(shapeData.points)) {
    nextShapeData.points = translatePoints(shapeData.points);
  }

  if (Array.isArray(shapeData.vertices)) {
    nextShapeData.vertices = translatePoints(shapeData.vertices);
  }
  if (Array.isArray(shapeData.paths)) nextShapeData.paths = shapeData.paths.map(translatePoints);
  if (Array.isArray(shapeData.boundaryPoints)) nextShapeData.boundaryPoints = translatePoints(shapeData.boundaryPoints);
  if (shapeData.door) nextShapeData.door = {
    ...shapeData.door,
    jambs: translatePoints(shapeData.door.jambs),
    sides: shapeData.door.sides?.map(side => ({ ...side, position: translatePoints([side.position])[0] })),
  };

  return nextShapeData;
};

const translateShape = (shape, deltaX, deltaZ) => {
  const nextShape = { ...(shape ?? {}) };
  const translateNumber = (value, delta) =>
    Number.isFinite(Number(value)) ? Number(value) + delta : value;

  if (deltaX !== 0) {
    nextShape.minX = translateNumber(nextShape.minX, deltaX);
    nextShape.maxX = translateNumber(nextShape.maxX, deltaX);
  }

  if (deltaZ !== 0) {
    nextShape.minZ = translateNumber(nextShape.minZ, deltaZ);
    nextShape.maxZ = translateNumber(nextShape.maxZ, deltaZ);
  }

  nextShape.shapeData = translateShapeData(nextShape.shapeData, deltaX, deltaZ);
  return nextShape;
};

const translatePlace = (place, deltaX, deltaZ) => {
  const currentLocation = place?.location ?? {};
  const currentX = Number(currentLocation.x);
  const currentZ = Number(currentLocation.z);
  const currentBounds = getShapeBounds(place);
  const fallbackCenterX = (currentBounds.minX + currentBounds.maxX) / 2;
  const fallbackCenterZ = (currentBounds.minZ + currentBounds.maxZ) / 2;

  return {
    ...place,
    location: {
      ...currentLocation,
      x: (Number.isFinite(currentX) ? currentX : fallbackCenterX) + deltaX,
      z: (Number.isFinite(currentZ) ? currentZ : fallbackCenterZ) + deltaZ,
    },
    shape: translateShape(place?.shape, deltaX, deltaZ),
  };
};

const translatePlaceTree = (place, deltaX, deltaZ) => {
  const translatedPlace = translatePlace(place, deltaX, deltaZ);

  if (!Array.isArray(place?.composedOfChildren)) {
    return translatedPlace;
  }

  return {
    ...translatedPlace,
    composedOfChildren: place.composedOfChildren.map((child) =>
      translatePlaceTree(child, deltaX, deltaZ)
    ),
  };
};

const getEdgeIndexDistance = (index, edge, pointCount) => {
  const distanceTo = (targetIndex) => {
    const directDistance = Math.abs(index - targetIndex);
    return edge.isClosed ? Math.min(directDistance, pointCount - directDistance) : directDistance;
  };

  return Math.min(distanceTo(edge.fromIndex), distanceTo(edge.toIndex));
};

const getEdgeInfluenceWeight = (index, edge, pointCount) => {
  const distance = getEdgeIndexDistance(index, edge, pointCount);
  const influenceRadius = Math.max(2, Math.min(10, Math.round(pointCount * 0.06)));

  if (distance > influenceRadius) {
    return 0;
  }

  return 0.5 + 0.5 * Math.cos((Math.PI * distance) / (influenceRadius + 1));
};

const reshapePlaceEdge = (place, edge, deltaX, deltaZ) => {
  const { pointKey, points, nextShapeType, pathIndex } = getEditableShapePointState(place, edge.pathIndex);

  if (!Array.isArray(points) || points.length < 2) {
    return place;
  }

  const nextPoints = points.map((point, index) => {
    const weight = getEdgeInfluenceWeight(index, edge, points.length);

    if (weight <= 0) {
      return point;
    }

    return {
      ...point,
      x: Number.isFinite(Number(point?.x)) ? Number(point.x) + deltaX * weight : point?.x,
      z: Number.isFinite(Number(point?.z)) ? Number(point.z) + deltaZ * weight : point?.z,
    };
  });
  return applyShapePointsToPlace(place, pointKey, nextPoints, nextShapeType, pathIndex);
};

const applyShapePointsToPlace = (place, pointKey, nextPoints, nextShapeType = null, pathIndex = null) => {
  const shape = place?.shape ?? {};
  const shapeData = shape.shapeData && typeof shape.shapeData === 'object' ? shape.shapeData : {};
  if (pointKey === 'paths' && (!Number.isInteger(pathIndex) || !Array.isArray(shapeData.paths?.[pathIndex]))) {
    return place;
  }
  const nextShapeData = pointKey === 'paths'
    ? { ...shapeData, paths: shapeData.paths.map((points, index) => index === pathIndex ? nextPoints : points) }
    : { ...shapeData, [pointKey]: nextPoints };
  const allPoints = pointKey === 'paths' ? nextShapeData.paths.flat() : nextPoints;
  const finitePoints = allPoints
    .map((point) => ({ x: Number(point?.x), z: Number(point?.z) }))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.z));

  if (finitePoints.length < 2) {
    return place;
  }

  const bounds = getPointBounds(finitePoints);
  if (pointKey === 'paths') {
    const halfWidth = Math.max(0, Number(shapeData.width) || 0) / 2;
    bounds.minX -= halfWidth;
    bounds.maxX += halfWidth;
    bounds.minZ -= halfWidth;
    bounds.maxZ += halfWidth;
  }
  const width = Math.max(1, bounds.maxX - bounds.minX);
  const depth = Math.max(1, bounds.maxZ - bounds.minZ);

  return {
    ...place,
    location: {
      ...(place.location ?? {}),
      x: (bounds.minX + bounds.maxX) / 2,
      z: (bounds.minZ + bounds.maxZ) / 2,
      maxRadius: Math.max(Number(place.location?.maxRadius) || 0, width / 2, depth / 2),
    },
    shape: {
      ...shape,
      shapeType: nextShapeType || shape.shapeType,
      minX: bounds.minX,
      maxX: bounds.maxX,
      minZ: bounds.minZ,
      maxZ: bounds.maxZ,
      shapeData: nextShapeData,
    },
  };
};

const insertPlaceVertex = (place, edge, point) => {
  const { pointKey, points, nextShapeType, pathIndex } = getEditableShapePointState(place, edge.pathIndex);

  if (!Array.isArray(points) || points.length < 2) {
    return place;
  }

  const insertIndex = edge.fromIndex + 1;
  const insertedPoint = { x: point.x, z: point.z };
  const from = points[edge.fromIndex];
  const to = points[edge.toIndex];
  if (from?.y != null && to?.y != null && Number.isFinite(Number(from.y)) && Number.isFinite(Number(to.y))) {
    const dx = Number(to.x) - Number(from.x);
    const dz = Number(to.z) - Number(from.z);
    const lengthSquared = dx * dx + dz * dz;
    const t = lengthSquared > 0
      ? Math.max(0, Math.min(1, ((point.x - Number(from.x)) * dx + (point.z - Number(from.z)) * dz) / lengthSquared))
      : 0.5;
    insertedPoint.y = Number(from.y) + (Number(to.y) - Number(from.y)) * t;
  }
  const nextPoints = [
    ...points.slice(0, insertIndex),
    insertedPoint,
    ...points.slice(insertIndex),
  ];

  return applyShapePointsToPlace(place, pointKey, nextPoints, nextShapeType, pathIndex);
};

const removePlaceVertex = (place, vertexIndex, pathIndex = null) => {
  const shape = place?.shape ?? {};
  const { pointKey, points, nextShapeType } = getEditableShapePointState(place, pathIndex);
  const shapeType = normalizeShapeTypeName(shape.shapeType);
  const minPoints = shapeType === 'path' ? 2 : 3;

  if (!Array.isArray(points) || points.length <= minPoints || vertexIndex < 0 || vertexIndex >= points.length) {
    return place;
  }

  return applyShapePointsToPlace(
    place,
    pointKey,
    points.filter((_, index) => index !== vertexIndex),
    nextShapeType,
    pathIndex
  );
};

const movePlaceVertexFromState = (place, vertexIndex, deltaX, deltaZ, editState) => {
  const { pointKey, points, nextShapeType, pathIndex } = editState ?? getEditableShapePointState(place);

  if (!Array.isArray(points) || vertexIndex < 0 || vertexIndex >= points.length) {
    return place;
  }

  const nextPoints = points.map((point, index) => {
    if (index !== vertexIndex) {
      return point;
    }

    return {
      ...point,
      x: Number.isFinite(Number(point?.x)) ? Number(point.x) + deltaX : point?.x,
      z: Number.isFinite(Number(point?.z)) ? Number(point.z) + deltaZ : point?.z,
    };
  });

  return applyShapePointsToPlace(place, pointKey, nextPoints, nextShapeType, pathIndex);
};

const getDistanceToSegment = (point, segment) => {
  const dx = segment.x2 - segment.x1;
  const dy = segment.y2 - segment.y1;
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared > 0
    ? Math.max(0, Math.min(1, ((point.x - segment.x1) * dx + (point.y - segment.y1) * dy) / lengthSquared))
    : 0;
  const closestX = segment.x1 + dx * t;
  const closestY = segment.y1 + dy * t;

  return Math.hypot(point.x - closestX, point.y - closestY);
};

export const buildPlaceOverviewPlan = (
  place,
  nestedChildrenByParentId = {},
  showNestedChildren = false,
  lockedBounds = null,
  contextPlaces = [],
  expandedOverviewParentIds = [],
  focusBoundsPlaces = []
) => {
  const directChildren = place?.composedOfChildren ?? [];
  const expandedParentIdSet = new Set((Array.isArray(expandedOverviewParentIds) ? expandedOverviewParentIds : [])
    .map((idEntity) => String(idEntity ?? ''))
    .filter(Boolean));
  const nestedParentPlaces = expandedParentIdSet.size > 0
    ? directChildren.filter((child) => expandedParentIdSet.has(String(child?.idEntity ?? '')))
    : directChildren;
  const nestedChildren = showNestedChildren
    ? nestedParentPlaces.flatMap((child) =>
        (nestedChildrenByParentId[child?.idEntity] ?? child?.composedOfChildren ?? [])
          .filter((nestedChild) => nestedChild?.idEntity)
          .map((nestedChild) => ({
            ...nestedChild,
            overviewParentId: child.idEntity,
            overviewDepth: 2,
          }))
      )
    : [];
  const contextPlaceEntries = (Array.isArray(contextPlaces) ? contextPlaces : [])
    .filter((entry) => entry?.idEntity)
    .map((entry) => ({
      ...entry,
      overviewContextOnly: true,
    }));
  const placesById = new Map();
  [place, ...directChildren, ...nestedChildren, ...contextPlaceEntries]
    .filter((entry) => entry?.idEntity)
    .forEach((entry) => placesById.set(entry.idEntity, entry));
  const places = [...placesById.values()];

  if (places.length === 0) {
    return null;
  }

  const focusBoundsEntries = (Array.isArray(focusBoundsPlaces) ? focusBoundsPlaces : [])
    .filter((entry) => entry?.idEntity);
  const boundsPlaces = focusBoundsEntries.length > 0
    ? focusBoundsEntries
    : places.filter((entry) => entry.overviewContextOnly !== true);
  const calculatedBounds = (boundsPlaces.length > 0 ? boundsPlaces : places).reduce(
    (bounds, entry) => {
      const entryBounds = getShapeBounds(entry);
      return {
        minX: Math.min(bounds.minX, entryBounds.minX),
        maxX: Math.max(bounds.maxX, entryBounds.maxX),
        minZ: Math.min(bounds.minZ, entryBounds.minZ),
        maxZ: Math.max(bounds.maxZ, entryBounds.maxZ),
      };
    },
    { minX: Infinity, maxX: -Infinity, minZ: Infinity, maxZ: -Infinity }
  );
  const hasLockedBounds =
    lockedBounds &&
    [lockedBounds.minX, lockedBounds.maxX, lockedBounds.minZ, lockedBounds.maxZ]
      .every((value) => Number.isFinite(Number(value)));
  const contextBuffer = getOverviewContextBuffer(calculatedBounds);
  const overallBounds = hasLockedBounds ? lockedBounds : {
    minX: calculatedBounds.minX - contextBuffer, maxX: calculatedBounds.maxX + contextBuffer,
    minZ: calculatedBounds.minZ - contextBuffer, maxZ: calculatedBounds.maxZ + contextBuffer,
  };
  const width = Math.max(1, overallBounds.maxX - overallBounds.minX);
  const height = Math.max(1, overallBounds.maxZ - overallBounds.minZ);
  const padding = 24;
  const drawableSize = PLACE_OVERVIEW_SIZE - padding * 2;
  const scale = Math.min(drawableSize / width, drawableSize / height);
  const offsetX = padding + (drawableSize - width * scale) / 2;
  const offsetY = padding + (drawableSize - height * scale) / 2;
  const toCanvasX = (x) => offsetX + (x - overallBounds.minX) * scale;
  const toCanvasY = (z) => offsetY + (z - overallBounds.minZ) * scale;
  const nationIds = places
    .filter((entry) => String(entry?.placeType ?? '').trim().toLowerCase().includes('nation'))
    .map((entry) => entry.idEntity || getPlaceLabel(entry))
    .sort((left, right) => String(left).localeCompare(String(right)));

  return {
    width: PLACE_OVERVIEW_SIZE,
    height: PLACE_OVERVIEW_SIZE,
    nodes: places
      .map((entry) => {
        const bounds = getShapeBounds(entry);
        const nationColorIndex = nationIds.indexOf(entry.idEntity || getPlaceLabel(entry));
        const color = getOverviewColor(entry, nationColorIndex >= 0 ? nationColorIndex : null);
        const isSelected = isSameEntityId(entry.idEntity, place?.idEntity);
        const isChild = directChildren.some((child) => isSameEntityId(child?.idEntity, entry.idEntity));
        const isNestedChild = entry.overviewDepth === 2;
        const isContextOnly = entry.overviewContextOnly === true && !isSelected && !isChild && !isNestedChild;
        const shapePoints = getOverviewShapePoints(entry);
        const shapeType = normalizeShapeTypeName(entry?.shape?.shapeType ?? 'Box');
        const isRectangularShape = isRectangularOverviewShape(shapeType);
        const hasEditableClosedEdges = isRectangularShape || shapeType === 'polygon';
        const multipartPaths = shapeType === 'path' && Array.isArray(entry?.shape?.shapeData?.paths)
          ? entry.shape.shapeData.paths : null;
        const pointGroups = multipartPaths
          ? multipartPaths.map((points, pathIndex) => ({ points, pathIndex }))
          : [{ points: shapePoints, pathIndex: null }];
        const edgeSegments = [isRectangularShape, shapeType === 'polygon', shapeType === 'path'].some(Boolean)
          ? pointGroups.flatMap(({ points, pathIndex }) => points
              .map((point, index) => {
                const edgePointCount = points.length;
                const toIndex = index + 1;
                const nextPoint = points[toIndex] ?? (hasEditableClosedEdges ? points[0] : null);
                if (!nextPoint || (shapeType === 'path' && index === edgePointCount - 1)) {
                  return null;
                }

                return {
                  key: `${entry.idEntity}-path-${pathIndex}-edge-${index}-${toIndex % edgePointCount}`,
                  pathIndex,
                  fromIndex: index,
                  toIndex: toIndex % edgePointCount,
                  isClosed: hasEditableClosedEdges,
                  worldX1: point.x,
                  worldZ1: point.z,
                  worldX2: nextPoint.x,
                  worldZ2: nextPoint.z,
                  x1: toCanvasX(point.x),
                  y1: toCanvasY(point.z),
                  x2: toCanvasX(nextPoint.x),
                  y2: toCanvasY(nextPoint.z),
                };
              })
              .filter(Boolean))
          : [];
        return {
          id: entry.idEntity,
          label: getPlaceLabel(entry),
          x: toCanvasX(bounds.minX),
          y: toCanvasY(bounds.minZ),
          width: Math.max(0, (bounds.maxX - bounds.minX) * scale),
          height: Math.max(0, (bounds.maxZ - bounds.minZ) * scale),
          centerX: toCanvasX((bounds.minX + bounds.maxX) / 2),
          centerY: toCanvasY((bounds.minZ + bounds.maxZ) / 2),
          radius: Math.max(0, Math.min(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ) * scale / 2),
          shapeType: entry?.shape?.shapeType ?? 'Box',
          imageGeometryKind: shapeType === 'path' ? 'physical-path'
            : entry.buildingType || /^(building|structure|fortification|wall|fence|gate|gatehouse|bridge|tower|transit point)$/i.test(entry.placeType || '')
              ? 'structure-footprint' : 'area-footprint',
          pathData: getMultipartPathData(entry, toCanvasX, toCanvasY),
          snapshotPathStrokeWidth: Number(entry?.shape?.shapeData?.width || entry.imagePathWidthFeet) > 0
            ? Math.max(1, Number(entry?.shape?.shapeData?.width || entry.imagePathWidthFeet) * scale) : 2.5,
          points: shapePoints.map((point) => `${toCanvasX(point.x)},${toCanvasY(point.z)}`).join(' '),
          vertexPoints: pointGroups.flatMap(({ points, pathIndex }) => points.map((point, index) => ({
            key: `${entry.idEntity}-path-${pathIndex}-vertex-${index}`,
            pathIndex,
            index,
            worldX: point.x,
            worldZ: point.z,
            x: toCanvasX(point.x),
            y: toCanvasY(point.z),
          }))),
          edgeSegments,
          fill: isSelected ? 'none' : color.fill,
          stroke: isSelected
            ? '#1f4e79'
            : isContextOnly
              ? color.stroke
              : color.preserveStroke
                ? color.stroke
                : isChild
                  ? '#7a4f17'
                  : isNestedChild
                    ? '#475569'
                    : color.stroke,
          isSelected,
          isChild,
          isNestedChild,
          isContextOnly,
          renderLayer: getOverviewRenderLayer(entry, color),
        };
      })
      .sort((left, right) => {
        if (left.renderLayer !== right.renderLayer) {
          return left.renderLayer - right.renderLayer;
        }

        return right.width * right.height - left.width * left.height;
      }),
    scale,
    overallBounds,
    toCanvasX,
    toCanvasY,
  };
};

function PlaceEditor({
  title = 'Place Editor',
  copy = 'Edit the generated or loaded place directly, then save it.',
  value,
  onChange,
  onSave,
  onDelete,
  onGenerateDescription,
  onGenerateChild,
  onGenerateAllSuggestions,
  onGenerateSuggestions,
  onGenerateInhabitantSuggestions,
  onGenerateInhabitant,
  onGenerateAllInhabitants,
  onGenerateInhabitantRelationships,
  onGenerateInhabitantPlaceRelationships,
  onGenerateItemSuggestions,
  onGenerateContainedItem,
  onGenerateAllContainedItems,
  onMoveChildPlace,
  onMovePlace,
  onDuplicateChildPlace,
  onRegeneratePath,
  onLoadOverviewPlace,
  onLoadOverviewContext,
  image = {},
  canGenerateImage = false,
  canSaveImage = false,
  onGenerateImage,
  onSaveImage,
  isSaving,
  isGeneratingDescription,
  isGeneratingSuggestions,
  isGeneratingInhabitantSuggestions,
  isGeneratingInhabitantRelationships,
  isGeneratingItemSuggestions,
  isCreatingAllContainedItems,
  saveMessage,
  getMasterTypeOptions,
  placeTypeOptions = [],
  zoneTypeOptions = [],
  buildingTypeOptions = [],
  transitPointTypeOptions = [],
  currencyOptions = [],
}) {
  const [generatingIndex, setGeneratingIndex] = useState(null);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [childSuggestionPrompt, setChildSuggestionPrompt] = useState('');
  const [descriptionGuidance, setDescriptionGuidance] = useState('');
  const [generatingInhabitantIndex, setGeneratingInhabitantIndex] = useState(null);
  const [isGeneratingAllInhabitants, setIsGeneratingAllInhabitants] = useState(false);
  const [generatingItemIndex, setGeneratingItemIndex] = useState(null);
  const [selectedOverviewPlaceId, setSelectedOverviewPlaceId] = useState('');
  const [overviewDrag, setOverviewDrag] = useState(null);
  const [overviewUndoStack, setOverviewUndoStack] = useState([]);
  const [overviewZoom, setOverviewZoom] = useState(1);
  const [selectedOverviewVertex, setSelectedOverviewVertex] = useState(null);
  const [showNestedOverviewChildren, setShowNestedOverviewChildren] = useState(false);
  const [overviewExpansionFocusPlaceId, setOverviewExpansionFocusPlaceId] = useState('');
  const [overviewNestedChildrenByParentId, setOverviewNestedChildrenByParentId] = useState({});
  const [overviewParentContextById, setOverviewParentContextById] = useState({});
  const [spatialOverviewContext, setSpatialOverviewContext] = useState({ placeId: '', key: '', places: [], error: '' });
  const [isLoadingNestedOverviewChildren, setIsLoadingNestedOverviewChildren] = useState(false);
  const [overviewLockedBounds, setOverviewLockedBounds] = useState(null);
  const [isDuplicateOverviewModalOpen, setIsDuplicateOverviewModalOpen] = useState(false);
  const [duplicateOverviewName, setDuplicateOverviewName] = useState('');
  const [isRegeneratingOverviewPath, setIsRegeneratingOverviewPath] = useState(false);
  const [naturalPathTarget, setNaturalPathTarget] = useState(null);
  const [naturalPathPreview, setNaturalPathPreview] = useState(null);
  const [isDuplicatingOverviewPlace, setIsDuplicatingOverviewPlace] = useState(false);
  const [isPreparingImageReference, setIsPreparingImageReference] = useState(false);
  const overviewFrameRef = useRef(null);
  const previousOverviewZoomRef = useRef(1);
  const overviewZoomFocusRef = useRef(null);
  const overviewLatestDragPlaceRef = useRef(null);
  const overviewSuppressNextClickRef = useRef(false);
  const place = value ?? createEmptyPlace();
  const isDraggingOverview = Boolean(overviewDrag);
  const spatialContextKey = JSON.stringify({ idEntity: place.idEntity, location: place.location, shape: place.shape });
  const loadSpatialOverviewContext = useCallback((imageReference = false) => new Promise((resolve, reject) => {
    if (!onLoadOverviewContext) { resolve([]); return; }
    onLoadOverviewContext({ ...JSON.parse(spatialContextKey), imageReference }, (error, result) => {
      if (error || result?.error || !Array.isArray(result?.places)) {
        reject(new Error(result?.error?.message || error?.message || 'Nearby map features could not be loaded.'));
      } else resolve(result.places);
    });
  }), [onLoadOverviewContext, spatialContextKey]);

  useEffect(() => {
    if (!onLoadOverviewContext || !place.idEntity || isDraggingOverview) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      loadSpatialOverviewContext().then(places => {
        if (!cancelled) setSpatialOverviewContext({ placeId: place.idEntity, key: spatialContextKey, places, error: '' });
      }).catch(error => {
        if (!cancelled) setSpatialOverviewContext(current => ({
          placeId: place.idEntity, key: spatialContextKey,
          places: current.placeId === place.idEntity ? current.places : [], error: error.message,
        }));
      });
    }, 300);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [isDraggingOverview, loadSpatialOverviewContext, onLoadOverviewContext, place.idEntity, spatialContextKey]);

  useEffect(() => {
    setNaturalPathTarget(null);
    setOverviewLockedBounds(null);
    setOverviewZoom(1);
    setSelectedOverviewPlaceId('');
    setSelectedOverviewVertex(null);
    setOverviewUndoStack([]);
  }, [place.idEntity]);
  const childSuggestionPromptId = `child-place-suggestion-prompt-${place.idEntity || 'draft'}`;

  const updateField = (field, nextValue) => {
    if (field === 'placeType') {
      const isTransitPoint = String(nextValue ?? '').trim().toLowerCase() === 'transit point';
      onChange({
        ...place,
        placeType: nextValue,
        transitPointType: isTransitPoint ? place.transitPointType : '',
      });
      return;
    }

    onChange({
      ...place,
      [field]: NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue,
    });
  };

  const updateLocation = (field, nextValue) => {
    const numericNextValue = LOCATION_NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue;
    const shouldTranslateShape = ['x', 'z'].includes(field) && Number.isFinite(Number(numericNextValue));
    const currentValue = Number(place.location?.[field]);
    const delta = shouldTranslateShape && Number.isFinite(currentValue)
      ? Number(numericNextValue) - currentValue
      : 0;

    onChange({
      ...place,
      location: {
        ...place.location,
        [field]: numericNextValue,
      },
      shape: shouldTranslateShape && delta !== 0
        ? translateShape(place.shape, field === 'x' ? delta : 0, field === 'z' ? delta : 0)
        : place.shape,
    });
  };

  const updateShape = (field, nextValue) => {
    onChange({
      ...place,
      shape: {
        ...place.shape,
        [field]: SHAPE_NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue,
      },
    });
  };

  const updateShapeData = (jsonText) => {
    try {
      const parsed = JSON.parse(jsonText);
      onChange({ ...place, shape: { ...place.shape, shapeData: parsed } });
    } catch {
      // let user keep typing; don't clobber on invalid JSON mid-edit
    }
  };

  const updateSuggestionField = (index, field, nextValue) => {
    onChange({
      ...place,
      childPlaceSuggestions: (place.childPlaceSuggestions ?? []).map((s, i) =>
        i === index
          ? {
              ...s,
              [field]: nextValue,
              ...(field === 'placeType' && String(nextValue ?? '').trim().toLowerCase() !== 'transit point'
                ? { transitPointType: '' }
                : {}),
            }
          : s
      ),
    });
  };

  const addSuggestion = () => {
    onChange({
      ...place,
      childPlaceSuggestions: [
        ...(place.childPlaceSuggestions ?? []),
        { name: '', placeType: '', zoneType: '', buildingType: '', transitPointType: '', description: '' },
      ],
    });
  };

  const removeSuggestion = (index) => {
    onChange({
      ...place,
      childPlaceSuggestions: (place.childPlaceSuggestions ?? []).filter((_, i) => i !== index),
    });
  };

  const updateInhabitantSuggestionField = (index, field, nextValue) => {
    onChange({
      ...place,
      inhabitantSuggestions: (place.inhabitantSuggestions ?? []).map((s, i) =>
        i === index ? { ...s, [field]: field === 'level' && nextValue !== '' ? Number(nextValue) : nextValue } : s
      ),
    });
  };

  const addInhabitantSuggestion = () => {
    onChange({
      ...place,
      inhabitantSuggestions: [
        ...(place.inhabitantSuggestions ?? []),
        {
          name: '',
          level: 1,
          title: '',
          beingType: '',
          description: '',
        },
      ],
    });
  };

  const removeInhabitantSuggestion = (index) => {
    onChange({
      ...place,
      inhabitantSuggestions: (place.inhabitantSuggestions ?? []).filter((_, i) => i !== index),
    });
  };

  const updateItemSuggestionField = (index, field, nextValue) => {
    onChange({
      ...place,
      itemSuggestions: (place.itemSuggestions ?? []).map((s, i) =>
        i === index ? { ...s, [field]: field === 'level' && nextValue !== '' ? Number(nextValue) : nextValue } : s
      ),
    });
  };

  const addItemSuggestion = () => {
    onChange({
      ...place,
      itemSuggestions: [
        ...(place.itemSuggestions ?? []),
        { name: '', level: 1, description: '' },
      ],
    });
  };

  const removeItemSuggestion = (index) => {
    onChange({
      ...place,
      itemSuggestions: (place.itemSuggestions ?? []).filter((_, i) => i !== index),
    });
  };

  const updateCurrencyRow = (index, field, nextValue) => {
    onChange({
      ...place,
      currencies: (place.currencies ?? []).map((currency, currentIndex) =>
        currentIndex === index
          ? {
              ...currency,
              [field]:
                CURRENCY_NUMERIC_FIELDS.has(field) && nextValue !== '' ? Number(nextValue) : nextValue,
            }
          : currency
      ),
    });
  };

  const addCurrencyRow = () => {
    onChange({
      ...place,
      currencies: [
        ...(place.currencies ?? []),
        {
          idCurrency: '',
          quantity: '',
        },
      ],
    });
  };

  const removeCurrencyRow = (index) => {
    onChange({
      ...place,
      currencies: (place.currencies ?? []).filter((_, currentIndex) => currentIndex !== index),
    });
  };

  const handleCopyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(place, null, 2));
  };

  const location = place.location ?? { x: 0, y: 0, z: 0, maxRadius: 0 };
  const shape = place.shape ?? { shapeType: 'Box', minX: 0, maxX: 0, minY: 0, maxY: 0, minZ: 0, maxZ: 0, shapeData: {} };
  const xDistanceMiles = getAxisDistanceMiles(shape.minX, shape.maxX);
  const yDistanceMiles = getAxisDistanceMiles(shape.minY, shape.maxY);
  const zDistanceMiles = getAxisDistanceMiles(shape.minZ, shape.maxZ);
  const xzAreaSquareMiles = Number.isFinite(xDistanceMiles) && Number.isFinite(zDistanceMiles)
    ? xDistanceMiles * zDistanceMiles
    : null;
  const childPlaceSuggestions = Array.isArray(place.childPlaceSuggestions) ? place.childPlaceSuggestions : [];
  const inhabitantSuggestions = Array.isArray(place.inhabitantSuggestions) ? place.inhabitantSuggestions : [];
  const itemSuggestions = Array.isArray(place.itemSuggestions) ? place.itemSuggestions : [];
  const containedItems = Array.isArray(place.containedItems) ? place.containedItems : [];
  const currencies = Array.isArray(place.currencies) ? place.currencies : [];
  const savedInhabitants = Array.isArray(place.savedInhabitants) ? place.savedInhabitants : [];
  const directOverviewChildIds = useMemo(
    () => (place.composedOfChildren ?? []).map((child) => child?.idEntity).filter(Boolean),
    [place.composedOfChildren]
  );
  const selectedDirectOverviewChild = useMemo(
    () => (place.composedOfChildren ?? []).find((child) =>
      selectedOverviewPlaceId && isSameEntityId(child?.idEntity, selectedOverviewPlaceId)
    ) ?? null,
    [place.composedOfChildren, selectedOverviewPlaceId]
  );
  const overviewExpansionFocusPlace = useMemo(
    () => (place.composedOfChildren ?? []).find((child) =>
      overviewExpansionFocusPlaceId && isSameEntityId(child?.idEntity, overviewExpansionFocusPlaceId)
    ) ?? null,
    [overviewExpansionFocusPlaceId, place.composedOfChildren]
  );
  const expandedOverviewParentIds = useMemo(
    () => showNestedOverviewChildren
      ? overviewExpansionFocusPlace?.idEntity
        ? [overviewExpansionFocusPlace.idEntity]
        : directOverviewChildIds
      : [],
    [directOverviewChildIds, overviewExpansionFocusPlace, showNestedOverviewChildren]
  );
  const expandedOverviewParents = useMemo(
    () => {
      const expandedIdSet = new Set(expandedOverviewParentIds.map((idEntity) => String(idEntity ?? '')));
      return (place.composedOfChildren ?? []).filter((child) => expandedIdSet.has(String(child?.idEntity ?? '')));
    },
    [expandedOverviewParentIds, place.composedOfChildren]
  );
  const overviewNestedChildren = useMemo(
    () =>
      expandedOverviewParents.flatMap(
        (parentPlace) =>
          overviewNestedChildrenByParentId[parentPlace.idEntity] ??
          parentPlace.composedOfChildren ??
          []
      ),
    [expandedOverviewParents, overviewNestedChildrenByParentId]
  );
  const overviewParentIds = useMemo(
    () => (Array.isArray(place.composedOfParents) ? place.composedOfParents : [])
      .map((parentPlace) => parentPlace?.idEntity)
      .filter((idEntity) => idEntity && !isSameEntityId(idEntity, place.idEntity)),
    [place.composedOfParents, place.idEntity]
  );
  const overviewParentContextSources = useMemo(
    () => overviewParentIds
      .map((parentId) => overviewParentContextById[parentId])
      .filter(Boolean)
      .flatMap((parentPlace) => [
        ...(Array.isArray(parentPlace.siblingPlaces) ? parentPlace.siblingPlaces : []),
        ...(Array.isArray(parentPlace.adjacentPlaces) ? parentPlace.adjacentPlaces : []),
      ]),
    [overviewParentContextById, overviewParentIds]
  );
  const overviewContextPlaces = useMemo(() => {
    return collectOverviewContextPlaces({
      place,
      directChildIds: directOverviewChildIds,
      nestedChildren: overviewNestedChildren,
      expansionFocusPlace: overviewExpansionFocusPlace,
      parentContextPlaces: overviewParentContextSources,
      spatialContextPlaces: spatialOverviewContext.placeId === place.idEntity ? spatialOverviewContext.places : [],
    });
  }, [directOverviewChildIds, overviewExpansionFocusPlace, overviewNestedChildren, overviewParentContextSources, place, spatialOverviewContext]);
  const overviewFocusBoundsPlaces = useMemo(
    () => [],
    []
  );
  const ageOptions = getMasterTypeOptions ? getMasterTypeOptions('age-multiple') : [];
  const seasonOptions = getMasterTypeOptions ? getMasterTypeOptions('season-multiple') : [];
  const beingTypeOptions = getMasterTypeOptions ? getMasterTypeOptions('beingType') : [];
  const isContainedItemActionDisabled = Boolean(isGeneratingItemSuggestions || isCreatingAllContainedItems);
  const isTransitPoint = String(place.placeType ?? '').trim().toLowerCase() === 'transit point';
  const overviewPlan = useMemo(
    () => buildPlaceOverviewPlan(
      place,
      overviewNestedChildrenByParentId,
      showNestedOverviewChildren,
      overviewLockedBounds,
      overviewContextPlaces,
      expandedOverviewParentIds,
      overviewFocusBoundsPlaces
    ),
    [expandedOverviewParentIds, overviewContextPlaces, overviewFocusBoundsPlaces, overviewLockedBounds, overviewNestedChildrenByParentId, place, showNestedOverviewChildren]
  );
  const selectedOverviewNode = useMemo(() => {
    const selectedId = selectedOverviewPlaceId || place.idEntity;
    return overviewPlan?.nodes.find((node) => node.id === selectedId) ?? null;
  }, [overviewPlan, place.idEntity, selectedOverviewPlaceId]);
  const overviewViewBox = useMemo(() => {
    const width = overviewPlan?.width ?? PLACE_OVERVIEW_SIZE;
    const height = overviewPlan?.height ?? PLACE_OVERVIEW_SIZE;
    const zoom = Math.max(PLACE_OVERVIEW_MIN_ZOOM, overviewZoom);
    if (zoom > 1) {
      return { x: 0, y: 0, width, height };
    }

    const viewWidth = width / zoom;
    const viewHeight = height / zoom;
    const zoomFocus = overviewZoomFocusRef.current;
    const focusX = zoomFocus
      ? zoomFocus.x
      : selectedOverviewNode
      ? selectedOverviewNode.x + selectedOverviewNode.width / 2
      : width / 2;
    const focusY = zoomFocus
      ? zoomFocus.y
      : selectedOverviewNode
      ? selectedOverviewNode.y + selectedOverviewNode.height / 2
      : height / 2;

    return {
      x: viewWidth <= width
        ? Math.max(0, Math.min(width - viewWidth, focusX - viewWidth / 2))
        : focusX - viewWidth / 2,
      y: viewHeight <= height
        ? Math.max(0, Math.min(height - viewHeight, focusY - viewHeight / 2))
        : focusY - viewHeight / 2,
      width: viewWidth,
      height: viewHeight,
    };
  }, [overviewPlan, overviewZoom, selectedOverviewNode]);
  const overviewSvgStyle = overviewZoom > 1
    ? {
        width: `${overviewZoom * 100}%`,
        height: `${overviewZoom * 100}%`,
        minHeight: `${overviewZoom * PLACE_OVERVIEW_SIZE}px`,
      }
    : undefined;
  const overviewHandleScale = Math.max(1, overviewZoom);
  const getOverviewShapeStrokeWidth = (node) => (node?.isSelected ? 2.75 : 1.5) / overviewHandleScale;
  const getOverviewPathStrokeWidth = (node) => (node?.isSelected ? 4 : 2.5) / overviewHandleScale;
  const overviewEdgeHitStrokeWidth = 14 / overviewHandleScale;
  const overviewVertexRadius = 3.25 / overviewHandleScale;
  const overviewVertexHitRadius = 5 / overviewHandleScale;
  const overviewSnapRadius = 12 / overviewHandleScale;
  const selectedOverviewVertexRadius = 4.5 / overviewHandleScale;
  const overviewVertexStrokeWidth = 1.2 / overviewHandleScale;
  const selectedOverviewVertexStrokeWidth = 1.8 / overviewHandleScale;

  const getOverviewViewportCenter = useCallback(() => {
    if (!overviewPlan) {
      return null;
    }

    const frame = overviewFrameRef.current;
    if (frame && overviewZoom > 1 && frame.scrollWidth > 0 && frame.scrollHeight > 0) {
      const scaleX = frame.scrollWidth / overviewPlan.width;
      const scaleY = frame.scrollHeight / overviewPlan.height;

      return {
        x: (frame.scrollLeft + frame.clientWidth / 2) / scaleX,
        y: (frame.scrollTop + frame.clientHeight / 2) / scaleY,
      };
    }

    return {
      x: overviewViewBox.x + overviewViewBox.width / 2,
      y: overviewViewBox.y + overviewViewBox.height / 2,
    };
  }, [overviewPlan, overviewViewBox, overviewZoom]);

  const changeOverviewZoom = useCallback((getNextZoom) => {
    const focus = getOverviewViewportCenter();
    if (focus) {
      overviewZoomFocusRef.current = focus;
    }

    setOverviewZoom((current) => Math.max(PLACE_OVERVIEW_MIN_ZOOM, getNextZoom(current)));
  }, [getOverviewViewportCenter]);

  useEffect(() => {
    setOverviewNestedChildrenByParentId((current) => {
      const validIds = new Set(directOverviewChildIds);
      const nextEntries = Object.entries(current).filter(([childId]) => validIds.has(childId));

      if (nextEntries.length === Object.keys(current).length) {
        return current;
      }

      return Object.fromEntries(nextEntries);
    });
  }, [directOverviewChildIds]);

  useEffect(() => {
    if (!overviewExpansionFocusPlaceId) {
      return;
    }

    if (!directOverviewChildIds.some((idEntity) => isSameEntityId(idEntity, overviewExpansionFocusPlaceId))) {
      setOverviewExpansionFocusPlaceId('');
    }
  }, [directOverviewChildIds, overviewExpansionFocusPlaceId]);

  useEffect(() => {
    if (!onLoadOverviewPlace || overviewParentIds.length === 0) {
      return undefined;
    }

    let isCancelled = false;
    const missingParentIds = overviewParentIds.filter((parentId) => !overviewParentContextById[parentId]);

    missingParentIds.forEach((parentId) => {
      onLoadOverviewPlace(parentId, (err, loadedPlace) => {
        if (isCancelled || err || !loadedPlace?.idEntity) {
          return;
        }

        setOverviewParentContextById((current) =>
          current[parentId]
            ? current
            : {
                ...current,
                [parentId]: loadedPlace,
              }
        );
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [onLoadOverviewPlace, overviewParentContextById, overviewParentIds]);

  useEffect(() => {
    if (!showNestedOverviewChildren || !onLoadOverviewPlace) {
      setIsLoadingNestedOverviewChildren(false);
      return undefined;
    }

    const missingChildren = expandedOverviewParents.filter(
      (child) =>
        child?.idEntity &&
        !Array.isArray(child?.composedOfChildren) &&
        !Object.prototype.hasOwnProperty.call(overviewNestedChildrenByParentId, child.idEntity)
    );

    if (missingChildren.length === 0) {
      setIsLoadingNestedOverviewChildren(false);
      return undefined;
    }

    let isActive = true;
    let remaining = missingChildren.length;
    setIsLoadingNestedOverviewChildren(true);

    missingChildren.forEach((child) => {
      onLoadOverviewPlace(child.idEntity, (err, loadedPlace) => {
        if (!isActive) {
          return;
        }

        setOverviewNestedChildrenByParentId((current) => ({
          ...current,
          [child.idEntity]: err || !loadedPlace || !Array.isArray(loadedPlace.composedOfChildren)
            ? []
            : loadedPlace.composedOfChildren,
        }));

        remaining -= 1;
        if (remaining === 0) {
          setIsLoadingNestedOverviewChildren(false);
        }
      });
    });

    return () => {
      isActive = false;
    };
  }, [
    directOverviewChildIds,
    expandedOverviewParents,
    onLoadOverviewPlace,
    overviewNestedChildrenByParentId,
    showNestedOverviewChildren,
  ]);

  useEffect(() => {
    const frame = overviewFrameRef.current;
    if (!frame || !overviewPlan) {
      return undefined;
    }

    if (previousOverviewZoomRef.current === overviewZoom) {
      return undefined;
    }

    previousOverviewZoomRef.current = overviewZoom;
    const animationFrame = window.requestAnimationFrame(() => {
      if (overviewZoom <= 1) {
        frame.scrollLeft = 0;
        frame.scrollTop = 0;
        overviewZoomFocusRef.current = null;
        return;
      }

      const zoomFocus = overviewZoomFocusRef.current;
      const focusX = zoomFocus
        ? zoomFocus.x
        : selectedOverviewNode
        ? selectedOverviewNode.x + selectedOverviewNode.width / 2
        : overviewPlan.width / 2;
      const focusY = zoomFocus
        ? zoomFocus.y
        : selectedOverviewNode
        ? selectedOverviewNode.y + selectedOverviewNode.height / 2
        : overviewPlan.height / 2;
      const scaleX = frame.scrollWidth / overviewPlan.width;
      const scaleY = frame.scrollHeight / overviewPlan.height;
      const maxScrollLeft = Math.max(0, frame.scrollWidth - frame.clientWidth);
      const maxScrollTop = Math.max(0, frame.scrollHeight - frame.clientHeight);

      frame.scrollLeft = Math.max(0, Math.min(maxScrollLeft, focusX * scaleX - frame.clientWidth / 2));
      frame.scrollTop = Math.max(0, Math.min(maxScrollTop, focusY * scaleY - frame.clientHeight / 2));
      overviewZoomFocusRef.current = null;
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [overviewPlan, overviewZoom, selectedOverviewNode]);
  const selectedOverviewPlace = useMemo(() => {
    const selectedId = selectedOverviewPlaceId || place.idEntity;
    return [place, ...(place.composedOfChildren ?? []), ...overviewNestedChildren, ...overviewContextPlaces]
      .find((entry) => entry?.idEntity === selectedId);
  }, [overviewContextPlaces, overviewNestedChildren, place, selectedOverviewPlaceId]);
  const canEditOverviewNode = (node) => node?.isChild || isSameEntityId(node?.id, place.idEntity);
  const canMoveOverviewNode = (node) => node?.isChild || isSameEntityId(node?.id, place.idEntity);
  const canDuplicateSelectedOverviewPlace = Boolean(
    onDuplicateChildPlace &&
    selectedOverviewNode?.isChild &&
    selectedOverviewPlace?.idEntity &&
    !overviewDrag
  );
  const canRegenerateSelectedOverviewPath = Boolean(
    onRegeneratePath &&
    selectedOverviewPlace?.idEntity &&
    isRoadPathDraft(selectedOverviewPlace) &&
    (
      isSameEntityId(selectedOverviewPlace.idEntity, place.idEntity) ||
      directOverviewChildIds.some((idEntity) => isSameEntityId(idEntity, selectedOverviewPlace.idEntity))
    ) &&
    !overviewDrag
  );
  const regenerateSelectedOverviewPath = () => {
    if (!canRegenerateSelectedOverviewPath || isRegeneratingOverviewPath || !onChange) {
      return;
    }

    const previousPlace = selectedOverviewPlace;
    setIsRegeneratingOverviewPath(true);
    onRegeneratePath(previousPlace, (err, result) => {
      setIsRegeneratingOverviewPath(false);
      if (err) {
        return;
      }

      const nextPlace = applyGeneratedRouteGeometry(previousPlace, result);
      if (!nextPlace) {
        return;
      }

      setOverviewUndoStack((current) => [
        ...current,
        { draftPlace: previousPlace, draftOnly: true },
      ].slice(-25));
      if (isSameEntityId(previousPlace.idEntity, place.idEntity)) {
        onChange(nextPlace);
      } else {
        onChange({
          ...place,
          composedOfChildren: (place.composedOfChildren ?? []).map((child) =>
            isSameEntityId(child?.idEntity, previousPlace.idEntity) ? nextPlace : child
          ),
        });
      }
      syncLoadedOverviewDescendantCache(nextPlace);
    });
  };
  const openDuplicateOverviewModal = () => {
    if (!canDuplicateSelectedOverviewPlace) {
      return;
    }

    setDuplicateOverviewName(`${getPlaceLabel(selectedOverviewPlace)} Copy`);
    setIsDuplicateOverviewModalOpen(true);
  };
  const closeDuplicateOverviewModal = () => {
    if (isDuplicatingOverviewPlace) {
      return;
    }

    setIsDuplicateOverviewModalOpen(false);
    setDuplicateOverviewName('');
  };
  const submitDuplicateOverviewPlace = (event) => {
    event.preventDefault();

    if (!canDuplicateSelectedOverviewPlace || isDuplicatingOverviewPlace) {
      return;
    }

    const trimmedName = duplicateOverviewName.trim();
    if (!trimmedName) {
      return;
    }

    setIsDuplicatingOverviewPlace(true);
    onDuplicateChildPlace(selectedOverviewPlace, trimmedName, (err) => {
      setIsDuplicatingOverviewPlace(false);

      if (!err) {
        setIsDuplicateOverviewModalOpen(false);
        setDuplicateOverviewName('');
      }
    });
  };
  const attachLoadedOverviewDescendants = useCallback((placeNode) => {
    if (!placeNode?.idEntity) {
      return placeNode;
    }

    const loadedChildren = overviewNestedChildrenByParentId[placeNode.idEntity];
    const childPlaces = Array.isArray(loadedChildren)
      ? loadedChildren
      : Array.isArray(placeNode.composedOfChildren)
        ? placeNode.composedOfChildren
        : null;

    if (!Array.isArray(childPlaces)) {
      return placeNode;
    }

    return {
      ...placeNode,
      composedOfChildren: childPlaces.map((child) => attachLoadedOverviewDescendants(child)),
    };
  }, [overviewNestedChildrenByParentId]);
  const attachOverviewDescendantsFromCache = useCallback((placeNode, nestedChildrenByParentId) => {
    if (!placeNode?.idEntity) {
      return placeNode;
    }

    const loadedChildren = nestedChildrenByParentId[placeNode.idEntity];
    const childPlaces = Array.isArray(loadedChildren)
      ? loadedChildren
      : Array.isArray(placeNode.composedOfChildren)
        ? placeNode.composedOfChildren
        : null;

    if (!Array.isArray(childPlaces)) {
      return placeNode;
    }

    return {
      ...placeNode,
      composedOfChildren: childPlaces.map((child) =>
        attachOverviewDescendantsFromCache(child, nestedChildrenByParentId)
      ),
    };
  }, []);
  const loadImageNestedChildrenByParentId = useCallback(async () => {
    const directChildren = Array.isArray(place.composedOfChildren) ? place.composedOfChildren : [];
    const missingChildren = directChildren.filter(
      (child) =>
        child?.idEntity &&
        !Array.isArray(child?.composedOfChildren)
    );

    if (!onLoadOverviewPlace || missingChildren.length === 0) {
      return overviewNestedChildrenByParentId;
    }

    const loadedEntries = await Promise.all(
      missingChildren.map(
        (child) =>
          new Promise((resolve, reject) => {
            onLoadOverviewPlace(child.idEntity, (err, loadedPlace) => {
              if (err || !loadedPlace || !Array.isArray(loadedPlace.composedOfChildren)) {
                reject(new Error(`Could not load sub-places of ${getPlaceLabel(child)} for the image reference.`));
                return;
              }
              resolve([
                child.idEntity,
                loadedPlace.composedOfChildren,
              ]);
            });
          })
      )
    );
    const nextNestedChildrenByParentId = {
      ...overviewNestedChildrenByParentId,
      ...Object.fromEntries(loadedEntries),
    };

    setOverviewNestedChildrenByParentId(nextNestedChildrenByParentId);
    return nextNestedChildrenByParentId;
  }, [onLoadOverviewPlace, overviewNestedChildrenByParentId, place.composedOfChildren]);
  const buildImageOverviewPlan = useCallback((nestedChildrenByParentId, contextPlaces = overviewContextPlaces) =>
    buildPlaceOverviewPlan(
      place,
      nestedChildrenByParentId,
      true,
      null,
      contextPlaces,
      directOverviewChildIds,
      contextPlaces.some(entry => entry.imageMapFrame)
        ? [place, ...contextPlaces.filter(entry => entry.imageMapFrame)] : overviewFocusBoundsPlaces
    ), [directOverviewChildIds, overviewContextPlaces, overviewFocusBoundsPlaces, place]);
  const getOverviewPlacesForImage = useCallback((imageOverviewPlan, nestedChildrenByParentId, contextPlaces = overviewContextPlaces) => {
    if (!imageOverviewPlan) {
      return [];
    }

    const imageNestedChildren = (place.composedOfChildren ?? []).flatMap((child) =>
      (nestedChildrenByParentId[child?.idEntity] ?? child?.composedOfChildren ?? [])
        .filter((nestedChild) => nestedChild?.idEntity)
        .map((nestedChild) => ({
          ...nestedChild,
          overviewParentId: child.idEntity,
          overviewDepth: 2,
        }))
    );
    const placeEntriesById = new Map(
      [place, ...(place.composedOfChildren ?? []), ...imageNestedChildren, ...contextPlaces]
        .filter((entry) => entry?.idEntity)
        .map((entry) => [String(entry.idEntity), entry])
    );

    return imageOverviewPlan.nodes
      .map((node) => {
        const entry = placeEntriesById.get(String(node.id));
        if (!entry) {
          return null;
        }

        return {
          ...entry,
          role: node.isSelected
            ? 'focus'
            : node.isChild
              ? 'direct-sub-place'
              : node.isNestedChild
                ? 'nested-sub-place'
                : node.isContextOnly
                  ? entry.imageMapFrame ? 'containing-map-frame' : 'visible-context'
                  : 'visible-place',
          depth: node.isNestedChild ? 2 : node.isChild ? 1 : 0,
          overview: {
            x: Math.round(node.x),
            y: Math.round(node.y),
            width: Math.round(node.width),
            height: Math.round(node.height),
            shapeType: node.shapeType,
            points: node.points || '',
            pathData: node.pathData || '',
            ...imageReferenceStyle(node),
            boundaryMeaning: node.imageGeometryKind === 'area-footprint' ? 'area extent only; no physical wall' : 'physical footprint',
          },
        };
      })
      .filter(Boolean);
  }, [overviewContextPlaces, place]);
  const generateImageFromOverview = useCallback(async () => {
    if (!onGenerateImage) {
      return;
    }

    setIsPreparingImageReference(true);
    try {
      const [imageNestedChildrenByParentId, spatialPlaces] = await Promise.all([
        loadImageNestedChildrenByParentId(), loadSpatialOverviewContext(true),
      ]);
      const contextPlaces = collectOverviewContextPlaces({ place, directChildIds: directOverviewChildIds,
        nestedChildren: Object.values(imageNestedChildrenByParentId).flat(),
        parentContextPlaces: overviewParentContextSources, spatialContextPlaces: spatialPlaces });
      const imageOverviewPlan = buildImageOverviewPlan(imageNestedChildrenByParentId, contextPlaces);
      if (!imageOverviewPlan) {
        throw new Error('Top-down overview is not available.');
      }

      const overviewSnapshotDataUrl = await renderOverviewPlanSnapshot(imageOverviewPlan);
      const base64Marker = ';base64,';
      const markerIndex = overviewSnapshotDataUrl.indexOf(base64Marker);
      if (markerIndex < 0) {
        throw new Error('Top-down overview snapshot is not in a usable image format.');
      }

      const shouldAutoSaveGeneratedImage = image?.hasCheckedSavedImage === true && !image?.src && Boolean(onSaveImage);
      onGenerateImage({
        place: attachOverviewDescendantsFromCache(place, imageNestedChildrenByParentId),
        visibleOverviewPlaces: getOverviewPlacesForImage(imageOverviewPlan, imageNestedChildrenByParentId, contextPlaces),
        overviewFrame: {
          width: imageOverviewPlan.width, height: imageOverviewPlan.height,
          worldBounds: imageOverviewPlan.overallBounds,
          contextPlaceId: contextPlaces.find(entry => entry.imageMapFrame)?.idEntity || place.idEntity,
          worldToDiagram: { scale: imageOverviewPlan.scale, offsetX: imageOverviewPlan.toCanvasX(0), offsetY: imageOverviewPlan.toCanvasY(0) },
        },
        overviewSnapshotDataUrl,
        overviewSnapshotMediaType: overviewSnapshotDataUrl.slice(5, markerIndex) || 'image/png',
        overviewSnapshotImageBase64: overviewSnapshotDataUrl.slice(markerIndex + base64Marker.length),
      }, {
        onGenerated: (generatedImage) => {
          if (shouldAutoSaveGeneratedImage && generatedImage?.src) {
            onSaveImage(generatedImage);
          }
        },
      });
    } catch (error) {
      window.alert(error?.message || 'Failed to capture the top-down overview snapshot.');
    } finally {
      setIsPreparingImageReference(false);
    }
  }, [
    attachOverviewDescendantsFromCache,
    buildImageOverviewPlan,
    getOverviewPlacesForImage,
    loadImageNestedChildrenByParentId,
    loadSpatialOverviewContext,
    spatialContextKey,
    directOverviewChildIds,
    overviewParentContextSources,
    image?.src,
    onGenerateImage,
    onSaveImage,
    place,
  ]);
  const syncLoadedOverviewDescendantCache = useCallback((movedPlace) => {
    const movedChildrenByParentId = new Map();
    const visit = (entry) => {
      if (!entry?.idEntity || !Array.isArray(entry.composedOfChildren)) {
        return;
      }

      movedChildrenByParentId.set(String(entry.idEntity), entry.composedOfChildren);
      entry.composedOfChildren.forEach(visit);
    };

    visit(movedPlace);

    if (movedChildrenByParentId.size === 0) {
      return;
    }

    setOverviewNestedChildrenByParentId((current) => {
      let didChange = false;
      const next = Object.fromEntries(
        Object.entries(current).map(([parentId, children]) => {
          const movedChildren = movedChildrenByParentId.get(String(parentId));

          if (!movedChildren || !Array.isArray(children)) {
            return [parentId, children];
          }

          didChange = true;
          return [parentId, movedChildren];
        })
      );

      return didChange ? next : current;
    });
  }, []);
  const selectOverviewPlace = (node) => {
    if (overviewSuppressNextClickRef.current) {
      overviewSuppressNextClickRef.current = false;
      return;
    }

    setSelectedOverviewPlaceId(node.id);
    setSelectedOverviewVertex(null);

    if (showNestedOverviewChildren && node?.isChild) {
      setOverviewExpansionFocusPlaceId(node.id);
    }
  };
  const toggleNestedOverviewChildren = (event) => {
    const shouldShow = event.target.checked;
    setShowNestedOverviewChildren(shouldShow);

    if (!shouldShow) {
      setOverviewExpansionFocusPlaceId('');
      return;
    }

    if (selectedDirectOverviewChild?.idEntity) {
      setOverviewExpansionFocusPlaceId(selectedDirectOverviewChild.idEntity);
    } else {
      setOverviewExpansionFocusPlaceId('');
    }
  };
  const findOverviewPlace = useCallback((idEntity) =>
    isSameEntityId(idEntity, place.idEntity)
      ? attachLoadedOverviewDescendants(place)
      : attachLoadedOverviewDescendants(
          (place.composedOfChildren ?? []).find((child) => isSameEntityId(child?.idEntity, idEntity))
        ), [attachLoadedOverviewDescendants, place]);
  const persistOverviewPlace = useCallback((changedPlace, { includeDescendants = false } = {}) => {
    if (!changedPlace) {
      return;
    }

    // Reshaping a boundary does not move its children. Exclude cached child
    // geometry from the save, or it can overwrite newer independent edits.
    const savePlace = includeDescendants ? changedPlace : { ...changedPlace, composedOfChildren: [] };
    if (isSameEntityId(changedPlace.idEntity, place.idEntity)) {
      if (onMovePlace) {
        onMovePlace(savePlace);
      }
      return;
    }

    if (onMoveChildPlace) {
      onMoveChildPlace(savePlace);
    }
  }, [onMoveChildPlace, onMovePlace, place.idEntity]);

  const updateOverviewPlace = useCallback((idEntity, updater) => {
    const undoPlace = findOverviewPlace(idEntity);
    if (!undoPlace || !onChange) {
      return null;
    }

    const nextPlace = updater(undoPlace);
    if (!nextPlace || nextPlace === undoPlace) {
      return null;
    }

    setOverviewUndoStack((current) => [...current, undoPlace].slice(-25));
    setOverviewLockedBounds(overviewPlan?.overallBounds ?? null);

    if (isSameEntityId(idEntity, place.idEntity)) {
      onChange(nextPlace);
    } else {
      onChange({
        ...place,
        composedOfChildren: (place.composedOfChildren ?? []).map((child) =>
          isSameEntityId(child?.idEntity, idEntity) ? nextPlace : child
        ),
      });
    }

    persistOverviewPlace(nextPlace);
    return nextPlace;
  }, [findOverviewPlace, onChange, overviewPlan, persistOverviewPlace, place]);

  const getOverviewPointerPoint = (event) => {
    const svg = event.currentTarget.ownerSVGElement || event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const viewBox = svg.viewBox.baseVal;

    return {
      x: viewBox.x + ((event.clientX - rect.left) / rect.width) * viewBox.width,
      y: viewBox.y + ((event.clientY - rect.top) / rect.height) * viewBox.height,
    };
  };

  const startOverviewDrag = (event, node) => {
    if (!canMoveOverviewNode(node) || !overviewPlan?.scale) {
      setSelectedOverviewPlaceId(node.id);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const svg = event.currentTarget.ownerSVGElement || event.currentTarget;
    svg.setPointerCapture?.(event.pointerId);
    const svgPoint = getOverviewPointerPoint(event);
    const basePlace = findOverviewPlace(node.id) ?? null;
    overviewLatestDragPlaceRef.current = null;
    setOverviewLockedBounds(overviewPlan.overallBounds ?? null);
    setSelectedOverviewPlaceId(node.id);
    setSelectedOverviewVertex(null);
    setOverviewDrag({
      mode: 'move',
      id: node.id,
      pointerId: event.pointerId,
      startX: svgPoint.x,
      startY: svgPoint.y,
      lastWorldX: 0,
      lastWorldZ: 0,
      scale: overviewPlan.scale,
      basePlace,
      undoPlace: basePlace,
    });
  };

  const startOverviewEdgeDrag = (event, node, edge) => {
    if (event.altKey && canMoveOverviewNode(node)) {
      startOverviewDrag(event, node);
      return;
    }

    if (!canEditOverviewNode(node) || !overviewPlan?.scale) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const svg = event.currentTarget.ownerSVGElement || event.currentTarget;
    svg.setPointerCapture?.(event.pointerId);
    const svgPoint = getOverviewPointerPoint(event);
    overviewLatestDragPlaceRef.current = null;
    setOverviewLockedBounds(overviewPlan.overallBounds ?? null);
    setSelectedOverviewPlaceId(node.id);
    setSelectedOverviewVertex(null);
    setOverviewDrag({
      mode: 'edge',
      id: node.id,
      edge,
      pointerId: event.pointerId,
      startX: svgPoint.x,
      startY: svgPoint.y,
      lastWorldX: 0,
      lastWorldZ: 0,
      scale: overviewPlan.scale,
      undoPlace: findOverviewPlace(node.id) ?? null,
    });
  };

  const startOverviewVertexDrag = (event, node, vertex) => {
    if (event.altKey && canMoveOverviewNode(node)) {
      startOverviewDrag(event, node);
      return;
    }

    if (!canEditOverviewNode(node) || !overviewPlan?.scale) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const svg = event.currentTarget.ownerSVGElement || event.currentTarget;
    svg.setPointerCapture?.(event.pointerId);
    const svgPoint = getOverviewPointerPoint(event);
    const nearestVertex = overviewPlan.nodes
      .filter((candidateNode) => canEditOverviewNode(candidateNode))
      .flatMap((candidateNode) =>
        candidateNode.vertexPoints.map((candidateVertex) => ({
          node: candidateNode,
          vertex: candidateVertex,
          distance: Math.hypot(candidateVertex.x - svgPoint.x, candidateVertex.y - svgPoint.y),
        }))
      )
      .filter((candidate) => Number.isFinite(candidate.distance) && candidate.distance <= overviewVertexHitRadius)
      .sort((left, right) => {
        const distanceDifference = left.distance - right.distance;
        if (Math.abs(distanceDifference) > 0.01) return distanceDifference;
        const isClickedVertex = (candidate) => candidate.node.id === node.id &&
          candidate.vertex.index === vertex.index && candidate.vertex.pathIndex === vertex.pathIndex;
        return Number(isClickedVertex(right)) - Number(isClickedVertex(left));
      })[0];
    const dragNode = nearestVertex?.node ?? node;
    const dragVertex = nearestVertex?.vertex ?? vertex;
    const dragPlace = findOverviewPlace(dragNode.id);
    const editableState = getEditableShapePointState(dragPlace, dragVertex.pathIndex);
    overviewLatestDragPlaceRef.current = null;
    setOverviewLockedBounds(overviewPlan.overallBounds ?? null);
    setSelectedOverviewPlaceId(dragNode.id);
    setSelectedOverviewVertex({ id: dragNode.id, index: dragVertex.index, pathIndex: dragVertex.pathIndex });
    setOverviewDrag({
      mode: 'vertex',
      id: dragNode.id,
      vertexIndex: dragVertex.index,
      pathIndex: dragVertex.pathIndex,
      pointerId: event.pointerId,
      startX: svgPoint.x,
      startY: svgPoint.y,
      lastWorldX: 0,
      lastWorldZ: 0,
      scale: overviewPlan.scale,
      pointKey: editableState.pointKey,
      basePoints: editableState.points.map((point) => ({ ...point })),
      nextShapeType: editableState.nextShapeType,
      basePlace: dragPlace ?? null,
      undoPlace: dragPlace ?? null,
    });
  };

  const getEdgeInsertPoint = (event, edge) => {
    const svgPoint = getOverviewPointerPoint(event);
    const dx = edge.x2 - edge.x1;
    const dy = edge.y2 - edge.y1;
    const lengthSquared = dx * dx + dy * dy;
    const t = lengthSquared > 0
      ? Math.max(0, Math.min(1, ((svgPoint.x - edge.x1) * dx + (svgPoint.y - edge.y1) * dy) / lengthSquared))
      : 0.5;

    return {
      x: edge.worldX1 + (edge.worldX2 - edge.worldX1) * t,
      z: edge.worldZ1 + (edge.worldZ2 - edge.worldZ1) * t,
    };
  };

  const insertOverviewVertex = (event, node, edge) => {
    if (!canEditOverviewNode(node)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const insertPoint = getEdgeInsertPoint(event, edge);
    const nextPlace = updateOverviewPlace(node.id, (currentPlace) =>
      insertPlaceVertex(currentPlace, edge, insertPoint)
    );

    if (nextPlace) {
      setSelectedOverviewPlaceId(node.id);
      setSelectedOverviewVertex({ id: node.id, index: edge.fromIndex + 1, pathIndex: edge.pathIndex });
    }
  };

  const insertNearestOverviewVertex = (event) => {
    if (!overviewPlan || overviewDrag) {
      return;
    }

    const svgPoint = getOverviewPointerPoint(event);
    const candidates = overviewPlan.nodes
      .filter((node) => canEditOverviewNode(node))
      .flatMap((node) => node.edgeSegments.map((edge) => ({
        node,
        edge,
        distance: getDistanceToSegment(svgPoint, edge),
      })))
      .sort((left, right) => left.distance - right.distance);
    const nearest = candidates[0];

    if (!nearest || nearest.distance > 18) {
      return;
    }

    insertOverviewVertex(event, nearest.node, nearest.edge);
  };

  const getSnappedVertexDelta = (dragState, deltaX, deltaZ) => {
    if (!overviewPlan || dragState?.mode !== 'vertex' || !Array.isArray(dragState.basePoints)) {
      return { deltaX, deltaZ };
    }

    const basePoint = dragState.basePoints[dragState.vertexIndex];
    const draggedX = Number(basePoint?.x) + deltaX;
    const draggedZ = Number(basePoint?.z) + deltaZ;

    if (!Number.isFinite(draggedX) || !Number.isFinite(draggedZ) || !Number.isFinite(overviewPlan.scale)) {
      return { deltaX, deltaZ };
    }

    const snapRadiusWorld = Math.max(1, overviewSnapRadius / overviewPlan.scale);
    const nearest = overviewPlan.nodes
      .flatMap((node) => node.vertexPoints
        .filter((vertex) => !isSameEntityId(node.id, dragState.id) || (
          Number.isInteger(dragState.pathIndex) && vertex.pathIndex !== dragState.pathIndex
        ))
        .map((vertex) => ({
          vertex,
          distance: Math.hypot(Number(vertex.worldX) - draggedX, Number(vertex.worldZ) - draggedZ),
        })))
      .filter((candidate) =>
        Number.isFinite(candidate.distance) &&
        candidate.distance <= snapRadiusWorld &&
        Number.isFinite(Number(candidate.vertex.worldX)) &&
        Number.isFinite(Number(candidate.vertex.worldZ))
      )
      .sort((left, right) => left.distance - right.distance)[0];

    if (!nearest) {
      return { deltaX, deltaZ };
    }

    return {
      deltaX: Number(nearest.vertex.worldX) - Number(basePoint.x),
      deltaZ: Number(nearest.vertex.worldZ) - Number(basePoint.z),
    };
  };

  const deleteSelectedOverviewVertex = useCallback(() => {
    if (!selectedOverviewVertex || overviewDrag) {
      return;
    }

    const nextPlace = updateOverviewPlace(selectedOverviewVertex.id, (currentPlace) =>
      removePlaceVertex(currentPlace, selectedOverviewVertex.index, selectedOverviewVertex.pathIndex)
    );

    if (nextPlace) {
      setSelectedOverviewVertex(null);
    }
  }, [overviewDrag, selectedOverviewVertex, updateOverviewPlace]);

  const updateOverviewDrag = (event) => {
    if (!overviewDrag || event.pointerId !== overviewDrag.pointerId || !onChange) {
      return;
    }

    const svgPoint = getOverviewPointerPoint(event);
    const worldDeltaX = (svgPoint.x - overviewDrag.startX) / overviewDrag.scale;
    const worldDeltaZ = (svgPoint.y - overviewDrag.startY) / overviewDrag.scale;
    const stepDeltaX = worldDeltaX - overviewDrag.lastWorldX;
    const stepDeltaZ = worldDeltaZ - overviewDrag.lastWorldZ;

    if (Math.abs(stepDeltaX) < 0.01 && Math.abs(stepDeltaZ) < 0.01) {
      return;
    }

    const vertexEditState = overviewDrag.mode === 'vertex'
      ? {
          pointKey: overviewDrag.pointKey,
          pathIndex: overviewDrag.pathIndex,
          points: overviewDrag.basePoints,
          nextShapeType: overviewDrag.nextShapeType,
        }
      : null;
    const vertexBasePlace = overviewDrag.mode === 'vertex'
      ? overviewDrag.basePlace ?? findOverviewPlace(overviewDrag.id)
      : null;
    const movedVertexPlace = vertexBasePlace && vertexEditState
      ? movePlaceVertexFromState(
          vertexBasePlace,
          overviewDrag.vertexIndex,
          worldDeltaX,
          worldDeltaZ,
          vertexEditState
        )
      : null;

    if (movedVertexPlace) {
      overviewLatestDragPlaceRef.current = movedVertexPlace;
    }

    if (isSameEntityId(overviewDrag.id, place.idEntity) && overviewDrag.mode === 'move') {
      const moveBasePlace = overviewDrag.basePlace ?? attachLoadedOverviewDescendants(place);
      const movedPlace = translatePlaceTree(moveBasePlace, worldDeltaX, worldDeltaZ);
      overviewLatestDragPlaceRef.current = movedPlace;
      syncLoadedOverviewDescendantCache(movedPlace);
      onChange(movedPlace);
    } else if (isSameEntityId(overviewDrag.id, place.idEntity) && overviewDrag.mode === 'edge') {
      onChange(reshapePlaceEdge(place, overviewDrag.edge, stepDeltaX, stepDeltaZ));
    } else if (isSameEntityId(overviewDrag.id, place.idEntity) && overviewDrag.mode === 'vertex') {
      onChange(movedVertexPlace ?? place);
    } else {
      let movedChildPlace = null;
      onChange({
        ...place,
        composedOfChildren: (place.composedOfChildren ?? []).map((child) => {
          if (!isSameEntityId(child?.idEntity, overviewDrag.id)) {
            return child;
          }

          movedChildPlace = overviewDrag.mode === 'edge'
            ? reshapePlaceEdge(child, overviewDrag.edge, stepDeltaX, stepDeltaZ)
            : overviewDrag.mode === 'vertex'
              ? movedVertexPlace ?? child
              : translatePlaceTree(overviewDrag.basePlace ?? attachLoadedOverviewDescendants(child), worldDeltaX, worldDeltaZ);
          return movedChildPlace;
        }),
      });

      if (movedChildPlace) {
        if (overviewDrag.mode === 'move') {
          syncLoadedOverviewDescendantCache(movedChildPlace);
        }
        overviewLatestDragPlaceRef.current = movedChildPlace;
      }
    }
    setOverviewDrag({
      ...overviewDrag,
      lastWorldX: worldDeltaX,
      lastWorldZ: worldDeltaZ,
    });
  };

  const finishOverviewDrag = (event) => {
    if (!overviewDrag || event.pointerId !== overviewDrag.pointerId) {
      return;
    }

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const didMove = Math.abs(overviewDrag.lastWorldX) >= 0.01 || Math.abs(overviewDrag.lastWorldZ) >= 0.01;
    let movedPlace = overviewLatestDragPlaceRef.current ?? findOverviewPlace(overviewDrag.id);

    if (didMove) {
      overviewSuppressNextClickRef.current = true;
      window.setTimeout(() => {
        overviewSuppressNextClickRef.current = false;
      }, 0);
    }

    if (didMove && overviewDrag.mode === 'vertex') {
      const snappedVertexDelta = getSnappedVertexDelta(overviewDrag, overviewDrag.lastWorldX, overviewDrag.lastWorldZ);
      const hasSnap =
        Math.abs(snappedVertexDelta.deltaX - overviewDrag.lastWorldX) >= 0.01 ||
        Math.abs(snappedVertexDelta.deltaZ - overviewDrag.lastWorldZ) >= 0.01;

      if (hasSnap) {
        const vertexEditState = {
          pointKey: overviewDrag.pointKey,
          pathIndex: overviewDrag.pathIndex,
          points: overviewDrag.basePoints,
          nextShapeType: overviewDrag.nextShapeType,
        };
        const vertexBasePlace = overviewDrag.basePlace ?? overviewDrag.undoPlace ?? findOverviewPlace(overviewDrag.id);
        const snappedPlace = vertexBasePlace
          ? movePlaceVertexFromState(
              vertexBasePlace,
              overviewDrag.vertexIndex,
              snappedVertexDelta.deltaX,
              snappedVertexDelta.deltaZ,
              vertexEditState
            )
          : null;

        if (snappedPlace) {
          movedPlace = snappedPlace;
          if (isSameEntityId(snappedPlace.idEntity, place.idEntity)) {
            onChange(snappedPlace);
          } else {
            onChange({
              ...place,
              composedOfChildren: (place.composedOfChildren ?? []).map((child) =>
                isSameEntityId(child?.idEntity, snappedPlace.idEntity) ? snappedPlace : child
              ),
            });
          }
        }
      }
    }

    setOverviewDrag(null);
    overviewLatestDragPlaceRef.current = null;

    if (movedPlace && didMove) {
      const includeDescendants = overviewDrag.mode === 'move';
      if (overviewDrag.undoPlace) {
        setOverviewUndoStack((current) => [...current, {
          draftPlace: overviewDrag.undoPlace, includeDescendants,
        }].slice(-25));
      }
      persistOverviewPlace(movedPlace, { includeDescendants });
    }
  };

  const undoOverviewEdit = useCallback(() => {
    const undoEntry = overviewUndoStack[overviewUndoStack.length - 1];
    const previousPlace = undoEntry?.draftPlace ?? undoEntry;
    if (!previousPlace || !onChange) {
      return;
    }

    setOverviewUndoStack((current) => current.slice(0, -1));
    setSelectedOverviewVertex(null);
    if (isSameEntityId(previousPlace.idEntity, place.idEntity)) {
      onChange(previousPlace);
    } else {
      onChange({
        ...place,
        composedOfChildren: (place.composedOfChildren ?? []).map((child) =>
          isSameEntityId(child?.idEntity, previousPlace.idEntity) ? previousPlace : child
        ),
      });
    }

    syncLoadedOverviewDescendantCache(previousPlace);
    if (!undoEntry?.draftOnly) {
      persistOverviewPlace(previousPlace, { includeDescendants: undoEntry?.includeDescendants === true });
    }
  }, [onChange, overviewUndoStack, persistOverviewPlace, place, syncLoadedOverviewDescendantCache]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const targetTagName = String(event.target?.tagName ?? '').toLowerCase();
      const isTextEditingTarget =
        event.target?.isContentEditable ||
        ['input', 'textarea', 'select'].includes(targetTagName);

      if (isTextEditingTarget) {
        return;
      }

      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedOverviewVertex) {
        event.preventDefault();
        deleteSelectedOverviewVertex();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'z') {
        if (overviewUndoStack.length === 0 || overviewDrag) {
          return;
        }

        event.preventDefault();
        undoOverviewEdit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteSelectedOverviewVertex, overviewDrag, overviewUndoStack, selectedOverviewVertex, undoOverviewEdit]);

  return (
    <section className="panel">
      <div className="panel-header-row">
        <div>
          <h2>{title}</h2>
          <p className="panel-copy">{copy}</p>
        </div>
        <div className="button-row compact">
          <button type="button" className="secondary-button button-with-icon" onClick={handleCopyJson}>
            <CopyIcon />
            <span>JSON</span>
          </button>
          {onDelete && place.idEntity && (
            <button type="button" className="secondary-button button-with-icon danger-button" onClick={onDelete} disabled={isSaving}>
              <TrashIcon />
              <span>Delete</span>
            </button>
          )}
          {onSave && (
            <button type="button" className="button-with-icon" onClick={onSave} disabled={isSaving}>
              <SaveIcon />
              <span>{isSaving ? 'Saving...' : 'Save'}</span>
            </button>
          )}
        </div>
      </div>
      {saveMessage && <div className="panel-status">{saveMessage}</div>}

      <div className="editor-layout">

        <section className="editor-section">
          <h3>Entity</h3>
          <div className="editor-grid editor-grid-two">
            <label htmlFor="place-name" className="editor-field">
              <span className="field-label">Name</span>
              <input
                id="place-name"
                className="text-input editor-input"
                value={place.name ?? ''}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </label>
            <label htmlFor="place-title" className="editor-field">
              <span className="field-label">Title</span>
              <input
                id="place-title"
                className="text-input editor-input"
                value={place.title ?? ''}
                onChange={(e) => updateField('title', e.target.value)}
              />
            </label>
            <label htmlFor="place-level" className="editor-field">
              <span className="field-label">Level</span>
              <input
                id="place-level"
                className="text-input editor-input"
                type="number"
                value={place.level ?? ''}
                onChange={(e) => updateField('level', e.target.value)}
              />
            </label>
            <label htmlFor="place-type" className="editor-field">
              <span className="field-label">Place Type</span>
              <select
                id="place-type"
                className="select-input"
                value={place.placeType ?? ''}
                onChange={(e) => updateField('placeType', e.target.value)}
              >
                <option value="">Select...</option>
                {placeTypeOptions.map((opt) => (
                  <option key={`place-type-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label htmlFor="place-zone-type" className="editor-field">
              <span className="field-label">Zone Type</span>
              <select
                id="place-zone-type"
                className="select-input"
                value={place.zoneType ?? ''}
                onChange={(e) => updateField('zoneType', e.target.value)}
              >
                <option value="">Select...</option>
                {zoneTypeOptions.map((opt) => (
                  <option key={`place-zone-type-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label htmlFor="place-building-type" className="editor-field">
              <span className="field-label">Building Type</span>
              <select
                id="place-building-type"
                className="select-input"
                value={place.buildingType ?? ''}
                onChange={(e) => updateField('buildingType', e.target.value)}
              >
                <option value="">Select...</option>
                {buildingTypeOptions.map((opt) => (
                  <option key={`place-building-type-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            {isTransitPoint && (
              <label htmlFor="place-transit-point-type" className="editor-field">
                <span className="field-label">Transit Point Type</span>
                <select
                  id="place-transit-point-type"
                  className="select-input"
                  value={place.transitPointType ?? ''}
                  onChange={(e) => updateField('transitPointType', e.target.value)}
                >
                  <option value="">Select...</option>
                  {transitPointTypeOptions.map((opt) => (
                    <option key={`place-transit-point-type-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <div className="editor-field editor-field-spaced">
            <div className="panel-header-row compact-header-row">
              <label htmlFor="place-description" className="field-label">Description</label>
              {onGenerateDescription && (
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  onClick={() => onGenerateDescription(descriptionGuidance)}
                  disabled={isSaving || isGeneratingDescription}
                  title="Generate a parent description from this place and its child places"
                >
                  <SparklesIcon />
                  <span>{isGeneratingDescription ? 'Generating...' : 'Generate Description'}</span>
                </button>
              )}
            </div>
            {onGenerateDescription && (
              <label htmlFor="place-description-guidance" className="editor-field place-description-guidance">
                <span className="field-label">Rewrite Prompt</span>
                <input
                  id="place-description-guidance"
                  className="text-input editor-input"
                  value={descriptionGuidance}
                  onChange={(e) => setDescriptionGuidance(e.target.value)}
                  placeholder="Example: firm ground, hardwood forest, no marsh reeds"
                  disabled={isSaving || isGeneratingDescription}
                />
              </label>
            )}
            <textarea
              id="place-description"
              className="text-area editor-textarea"
              value={place.description ?? ''}
              onChange={(e) => updateField('description', e.target.value)}
            />
            <PlacePresentationEditor place={place} updateField={updateField} />
          </div>
        </section>

        <section className="editor-section place-image-section">
          <div className="place-image-copy">
            <h3>Image</h3>
            {image.error && <div className="panel-status error-status">{image.error}</div>}
            {spatialOverviewContext.key === spatialContextKey && spatialOverviewContext.error &&
              <div className="panel-status error-status">{spatialOverviewContext.error} Generate Image will retry loading nearby map features.</div>}
          </div>
          <div className="place-image-panel">
            <div className="place-image-frame">
              {image.src ? (
                <MediaImage size="display" src={image.src} alt="Generated place approach view" />
              ) : (
                <span>{place.idEntity ? 'No image saved' : 'Save place first'}</span>
              )}
            </div>
            <div className="place-image-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={generateImageFromOverview}
                disabled={!canGenerateImage || !overviewPlan || isPreparingImageReference || image.isGenerating || image.isSaving}
                title={!overviewPlan ? 'Top-down overview is required' : 'Generate an image from this place and overview map'}
              >
                {isPreparingImageReference ? 'Preparing...' : image.isGenerating ? 'Generating...' : 'Generate Image'}
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => onSaveImage?.()}
                disabled={!canSaveImage || image.isGenerating || image.isSaving}
              >
                {image.isSaving ? 'Saving...' : 'Save Image'}
              </button>
            </div>
          </div>
        </section>

        <section className="editor-section">
          <h3>Timeline</h3>
          <div className="editor-grid editor-grid-four">
            <label htmlFor="place-create-age" className="editor-field">
              <span className="field-label">Create Age</span>
              <select
                id="place-create-age"
                className="select-input"
                value={place.createDateAge ?? ''}
                onChange={(e) => updateField('createDateAge', e.target.value)}
              >
                <option value="">Select...</option>
                {ageOptions.map((opt) => (
                  <option key={`place-create-age-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label htmlFor="place-create-year" className="editor-field">
              <span className="field-label">Create Year</span>
              <input
                id="place-create-year"
                className="text-input editor-input"
                type="number"
                value={place.createDateYear ?? ''}
                onChange={(e) => updateField('createDateYear', e.target.value)}
              />
            </label>
            <label htmlFor="place-create-season" className="editor-field">
              <span className="field-label">Create Season</span>
              <select
                id="place-create-season"
                className="select-input"
                value={place.createDateSeason ?? ''}
                onChange={(e) => updateField('createDateSeason', e.target.value)}
              >
                <option value="">Select...</option>
                {seasonOptions.map((opt) => (
                  <option key={`place-create-season-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label htmlFor="place-create-day" className="editor-field">
              <span className="field-label">Create Day</span>
              <input
                id="place-create-day"
                className="text-input editor-input"
                type="number"
                value={place.createDateDay ?? ''}
                onChange={(e) => updateField('createDateDay', e.target.value)}
              />
            </label>
            <label htmlFor="place-destroy-age" className="editor-field">
              <span className="field-label">Destroy Age</span>
              <select
                id="place-destroy-age"
                className="select-input"
                value={place.destroyDateAge ?? ''}
                onChange={(e) => updateField('destroyDateAge', e.target.value)}
              >
                <option value="">Select...</option>
                {ageOptions.map((opt) => (
                  <option key={`place-destroy-age-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label htmlFor="place-destroy-year" className="editor-field">
              <span className="field-label">Destroy Year</span>
              <input
                id="place-destroy-year"
                className="text-input editor-input"
                type="number"
                value={place.destroyDateYear ?? ''}
                onChange={(e) => updateField('destroyDateYear', e.target.value)}
              />
            </label>
            <label htmlFor="place-destroy-season" className="editor-field">
              <span className="field-label">Destroy Season</span>
              <select
                id="place-destroy-season"
                className="select-input"
                value={place.destroyDateSeason ?? ''}
                onChange={(e) => updateField('destroyDateSeason', e.target.value)}
              >
                <option value="">Select...</option>
                {seasonOptions.map((opt) => (
                  <option key={`place-destroy-season-${opt.value}`} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label htmlFor="place-destroy-day" className="editor-field">
              <span className="field-label">Destroy Day</span>
              <input
                id="place-destroy-day"
                className="text-input editor-input"
                type="number"
                value={place.destroyDateDay ?? ''}
                onChange={(e) => updateField('destroyDateDay', e.target.value)}
              />
            </label>
          </div>
        </section>

        <section className="editor-section">
          <h3>Location</h3>
          <div className="editor-grid editor-grid-four">
            <label htmlFor="place-loc-x" className="editor-field">
              <span className="field-label">X (East/West, ft)</span>
              <input
                id="place-loc-x"
                className="text-input editor-input"
                type="number"
                value={location.x ?? ''}
                onChange={(e) => updateLocation('x', e.target.value)}
              />
            </label>
            <label htmlFor="place-loc-y" className="editor-field">
              <span className="field-label">Y (Vertical, ft)</span>
              <input
                id="place-loc-y"
                className="text-input editor-input"
                type="number"
                value={location.y ?? ''}
                onChange={(e) => updateLocation('y', e.target.value)}
              />
            </label>
            <label htmlFor="place-loc-z" className="editor-field">
              <span className="field-label">Z (North/South, ft)</span>
              <input
                id="place-loc-z"
                className="text-input editor-input"
                type="number"
                value={location.z ?? ''}
                onChange={(e) => updateLocation('z', e.target.value)}
              />
            </label>
            <label htmlFor="place-max-radius" className="editor-field">
              <span className="field-label">Max Radius (ft)</span>
              <input
                id="place-max-radius"
                className="text-input editor-input"
                type="number"
                value={location.maxRadius ?? ''}
                onChange={(e) => updateLocation('maxRadius', e.target.value)}
              />
            </label>
          </div>
        </section>

        <EntityBiomeFields value={place.biomes || []} onChange={biomes => onChange({ ...place, biomes })} />

        <section className="editor-section">
          <h3>Shape</h3>
          <div className="editor-grid editor-grid-four">
            <label htmlFor="place-shape-type" className="editor-field">
              <span className="field-label">Shape Type</span>
              <select
                id="place-shape-type"
                className="select-input"
                value={shape.shapeType ?? ''}
                onChange={(e) => updateShape('shapeType', e.target.value)}
              >
                <option value="">Select...</option>
                {SHAPE_TYPES.map((st) => (
                  <option key={`shape-type-${st}`} value={st}>{st}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="editor-grid editor-grid-three" style={{ marginTop: '8px' }}>
            <label htmlFor="place-min-x" className="editor-field">
              <span className="field-label">Min X</span>
              <input id="place-min-x" className="text-input editor-input" type="number"
                value={shape.minX ?? ''} onChange={(e) => updateShape('minX', e.target.value)} />
            </label>
            <label htmlFor="place-max-x" className="editor-field">
              <span className="field-label">Max X</span>
              <input id="place-max-x" className="text-input editor-input" type="number"
                value={shape.maxX ?? ''} onChange={(e) => updateShape('maxX', e.target.value)} />
            </label>
            <div className="editor-field" />
            <label htmlFor="place-min-y" className="editor-field">
              <span className="field-label">Min Y</span>
              <input id="place-min-y" className="text-input editor-input" type="number"
                value={shape.minY ?? ''} onChange={(e) => updateShape('minY', e.target.value)} />
            </label>
            <label htmlFor="place-max-y" className="editor-field">
              <span className="field-label">Max Y</span>
              <input id="place-max-y" className="text-input editor-input" type="number"
                value={shape.maxY ?? ''} onChange={(e) => updateShape('maxY', e.target.value)} />
            </label>
            <div className="editor-field" />
            <label htmlFor="place-min-z" className="editor-field">
              <span className="field-label">Min Z</span>
              <input id="place-min-z" className="text-input editor-input" type="number"
                value={shape.minZ ?? ''} onChange={(e) => updateShape('minZ', e.target.value)} />
            </label>
            <label htmlFor="place-max-z" className="editor-field">
              <span className="field-label">Max Z</span>
              <input id="place-max-z" className="text-input editor-input" type="number"
                value={shape.maxZ ?? ''} onChange={(e) => updateShape('maxZ', e.target.value)} />
            </label>
          </div>
          <div className="editor-grid editor-grid-four" style={{ marginTop: '8px' }}>
            <label htmlFor="place-shape-x-miles" className="editor-field">
              <span className="field-label">X Distance (mi)</span>
              <input
                id="place-shape-x-miles"
                className="text-input editor-input"
                value={formatMiles(xDistanceMiles)}
                readOnly
              />
            </label>
            <label htmlFor="place-shape-y-miles" className="editor-field">
              <span className="field-label">Y Distance (mi)</span>
              <input
                id="place-shape-y-miles"
                className="text-input editor-input"
                value={formatMiles(yDistanceMiles)}
                readOnly
              />
            </label>
            <label htmlFor="place-shape-z-miles" className="editor-field">
              <span className="field-label">Z Distance (mi)</span>
              <input
                id="place-shape-z-miles"
                className="text-input editor-input"
                value={formatMiles(zDistanceMiles)}
                readOnly
              />
            </label>
            <label htmlFor="place-shape-xz-square-miles" className="editor-field">
              <span className="field-label">X/Z Area (sq mi)</span>
              <input
                id="place-shape-xz-square-miles"
                className="text-input editor-input"
                value={formatMiles(xzAreaSquareMiles)}
                readOnly
              />
            </label>
          </div>
          <label htmlFor="place-shape-data" className="editor-field editor-field-spaced">
            <span className="field-label">Shape Data (JSON)</span>
            {['door', 'gate'].includes(String(place.transitPointType).toLowerCase()) && (
              <span className="text-muted">Door geometry: set door.version to 1, door.open, door.heightFeet, two door.jambs
                and two door.sides with placeId and position (absolute X/Y/Z feet). The first side owns the door.
                Saving maintains its Connects relationships. North decreases Z; Y is elevation.</span>
            )}
            <textarea
              id="place-shape-data"
              className="text-area editor-textarea"
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
              value={JSON.stringify(shape.shapeData ?? {}, null, 2)}
              onChange={(e) => updateShapeData(e.target.value)}
            />
          </label>
        </section>

                <section className="editor-section">
          <div className="section-header">
            <h3>Contained Items</h3>
          </div>
          <div className="editor-table">
            <div className="editor-table-row editor-table-head place-contained-items-grid">
              <span>Name</span>
              <span>Level</span>
              <span>Title</span>
              <span>Description</span>
            </div>
            {containedItems.map((item, index) => (
              <div key={`contained-item-${item.idEntity || index}`} className="editor-table-row place-contained-items-grid">
                <span>{item.name || '(Unnamed Item)'}</span>
                <span>{item.level ?? ''}</span>
                <span>{item.title || ''}</span>
                <span>{item.description || ''}</span>
              </div>
            ))}
            {containedItems.length === 0 && (
              <div className="empty-grid">No contained items.</div>
            )}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Currency</h3>
            <button type="button" className="secondary-button button-with-icon" onClick={addCurrencyRow}>
              <span>Add Currency</span>
            </button>
          </div>
          <div className="editor-table">
            <div className="editor-table-row editor-table-head being-currency-grid">
              <span>Currency</span>
              <span>Quantity</span>
              <span className="table-action-head" title="Remove">
                <TrashIcon />
              </span>
            </div>
            {currencies.map((currency, index) => (
              <div key={`place-currency-${currency.idCurrency || index}`} className="editor-table-row being-currency-grid">
                <select
                  className="select-input"
                  value={currency.idCurrency ?? ''}
                  onChange={(event) => updateCurrencyRow(index, 'idCurrency', event.target.value)}
                >
                  <option value="">Select...</option>
                  {currencyOptions.map((option) => (
                    <option key={`place-currency-option-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  className="text-input editor-input"
                  type="number"
                  value={currency.quantity ?? ''}
                  onChange={(event) => updateCurrencyRow(index, 'quantity', event.target.value)}
                />
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove currency"
                  onClick={() => removeCurrencyRow(index)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {currencies.length === 0 && <div className="empty-grid">No currency.</div>}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Contained Item Suggestions</h3>
            <div className="button-row compact">
              {onGenerateItemSuggestions && (
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  onClick={onGenerateItemSuggestions}
                  disabled={!place.idEntity || isContainedItemActionDisabled}
                  title={!place.idEntity ? 'Save this place first' : 'Refresh the contained item suggestion list'}
                >
                  <SparklesIcon />
                  <span>{isGeneratingItemSuggestions ? 'Refreshing...' : 'Refresh Suggestions'}</span>
                </button>
              )}
              {onGenerateAllContainedItems && (
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  onClick={() => onGenerateAllContainedItems(itemSuggestions)}
                  disabled={!place.idEntity || isContainedItemActionDisabled || generatingItemIndex !== null || itemSuggestions.length === 0}
                  title={!place.idEntity ? 'Save this place first' : 'Create all current contained item suggestions'}
                >
                  <SparklesIcon />
                  <span>{isCreatingAllContainedItems ? 'Creating...' : 'Create All'}</span>
                </button>
              )}
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={addItemSuggestion}
                disabled={isContainedItemActionDisabled}
              >
                <span>Add Suggestion</span>
              </button>
            </div>
          </div>
          {!place.idEntity && onGenerateItemSuggestions && (
            <div className="panel-copy" style={{ marginBottom: '8px', color: '#888' }}>
              Save this place first to generate contained item suggestions using the place context.
            </div>
          )}
          <div className="editor-table">
            <div className="editor-table-row editor-table-head being-item-suggestions-grid">
              <span>Name</span>
              <span>Level</span>
              <span>Description</span>
              <span className="table-action-head" title="Create"><SparklesIcon /></span>
              <span className="table-action-head" title="Remove"><TrashIcon /></span>
            </div>
            {itemSuggestions.map((s, index) => (
              <div key={`place-item-suggestion-${index}`} className="editor-table-row being-item-suggestions-grid">
                <input
                  className="text-input editor-input"
                  value={s.name ?? ''}
                  placeholder="Name"
                  onChange={(e) => updateItemSuggestionField(index, 'name', e.target.value)}
                />
                <input
                  className="text-input editor-input"
                  type="number"
                  value={s.level ?? ''}
                  placeholder="Level"
                  onChange={(e) => updateItemSuggestionField(index, 'level', e.target.value)}
                />
                <textarea
                  className="text-area grid-textarea"
                  value={s.description ?? ''}
                  placeholder="Description"
                  onChange={(e) => updateItemSuggestionField(index, 'description', e.target.value)}
                />
                <button
                  type="button"
                  className="icon-button"
                  aria-label="Create contained item from suggestion"
                  title={!place.idEntity ? 'Save this place first' : 'Create and save this item'}
                  disabled={!place.idEntity || generatingItemIndex !== null || isContainedItemActionDisabled || !onGenerateContainedItem}
                  onClick={() => {
                    if (!onGenerateContainedItem) {
                      return;
                    }

                    setGeneratingItemIndex(index);
                    onGenerateContainedItem(s, index, () => setGeneratingItemIndex(null));
                  }}
                >
                  {generatingItemIndex === index ? (
                    <span style={{ fontSize: '10px', padding: '0 2px' }}>...</span>
                  ) : (
                    <SparklesIcon />
                  )}
                </button>
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove item suggestion"
                  disabled={isContainedItemActionDisabled}
                  onClick={() => removeItemSuggestion(index)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {itemSuggestions.length === 0 && (
              <div className="empty-grid">No contained item suggestions.</div>
            )}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Saved Inhabitants</h3>
            {(onGenerateInhabitantRelationships || onGenerateInhabitantPlaceRelationships) && (
              <div className="button-row compact">
                {onGenerateInhabitantRelationships && (
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    onClick={onGenerateInhabitantRelationships}
                    disabled={!place.idEntity || isGeneratingInhabitantRelationships || savedInhabitants.length < 2}
                    title={
                      !place.idEntity
                        ? 'Save this place first'
                        : savedInhabitants.length < 2
                          ? 'At least two saved inhabitants are required'
                          : 'Generate relationship suggestions between saved inhabitants'
                    }
                  >
                    <SparklesIcon />
                    <span>{isGeneratingInhabitantRelationships ? 'Generating...' : 'Being Relationships'}</span>
                  </button>
                )}
                {onGenerateInhabitantPlaceRelationships && (
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    onClick={onGenerateInhabitantPlaceRelationships}
                    disabled={!place.idEntity || isGeneratingInhabitantRelationships || savedInhabitants.length === 0}
                    title={
                      !place.idEntity
                        ? 'Save this place first'
                        : savedInhabitants.length === 0
                          ? 'At least one saved inhabitant is required'
                          : 'Generate relationship suggestions between saved inhabitants and this place tree'
                    }
                  >
                    <SparklesIcon />
                    <span>{isGeneratingInhabitantRelationships ? 'Generating...' : 'Place Relationships'}</span>
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="editor-table">
            <div className="editor-table-row saved-inhabitants-grid editor-table-head">
              <span>Name</span>
              <span>Title</span>
              <span>Level</span>
              <span>Being Type</span>
              <span>Description</span>
            </div>
            {savedInhabitants.map((being, index) => (
              <div key={`saved-inhabitant-${being.idEntity || index}`} className="editor-table-row saved-inhabitants-grid">
                <input
                  className="text-input editor-input"
                  value={being.name ?? ''}
                  readOnly
                />
                <input
                  className="text-input editor-input"
                  value={being.title ?? ''}
                  readOnly
                />
                <input
                  className="text-input editor-input"
                  value={being.level ?? ''}
                  readOnly
                />
                <input
                  className="text-input editor-input"
                  value={being.beingType ?? ''}
                  readOnly
                />
                <textarea
                  className="text-area grid-textarea"
                  value={being.description ?? ''}
                  readOnly
                />
              </div>
            ))}
            {savedInhabitants.length === 0 && (
              <div className="empty-grid">No saved inhabitants.</div>
            )}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Inhabitant Suggestions</h3>
            <div className="button-row compact">
              {onGenerateInhabitantSuggestions && (
                <>
                  <button
                    type="button"
                    className="secondary-button button-with-icon"
                    onClick={onGenerateInhabitantSuggestions}
                    disabled={!place.idEntity || isGeneratingInhabitantSuggestions || isGeneratingAllInhabitants}
                    title={!place.idEntity ? 'Save this place first' : 'Refresh the inhabitant suggestion list'}
                  >
                    <SparklesIcon />
                    <span>{isGeneratingInhabitantSuggestions ? 'Refreshing...' : 'Refresh Suggestions'}</span>
                  </button>
                </>
              )}
              {onGenerateAllInhabitants && (
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  onClick={() => {
                    setIsGeneratingAllInhabitants(true);
                    onGenerateAllInhabitants(inhabitantSuggestions, () => setIsGeneratingAllInhabitants(false));
                  }}
                  disabled={!place.idEntity || isGeneratingInhabitantSuggestions || isGeneratingAllInhabitants || generatingInhabitantIndex !== null || inhabitantSuggestions.length === 0}
                  title={!place.idEntity ? 'Save this place first' : 'Create all current inhabitant suggestions'}
                >
                  <SparklesIcon />
                  <span>{isGeneratingAllInhabitants ? 'Creating...' : 'Create All'}</span>
                </button>
              )}
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={addInhabitantSuggestion}
              >
                <span>Add Inhabitant</span>
              </button>
            </div>
          </div>
          {!place.idEntity && onGenerateInhabitantSuggestions && (
            <div className="panel-copy" style={{ marginBottom: '8px', color: '#888' }}>
              Save this place first to generate inhabitant suggestions using the place tree.
            </div>
          )}
            <div className="editor-table">
            <div className="editor-table-row inhabitant-suggestions-grid editor-table-head">
              <span>Name</span>
              <span>Level</span>
              <span>Title</span>
              <span>Being Type</span>
              <span>Description</span>
              <span className="table-action-head" title="Create"><SparklesIcon /></span>
              <span className="table-action-head" title="Remove"><TrashIcon /></span>
            </div>
            {inhabitantSuggestions.map((s, index) => (
              <div key={`inhabitant-suggestion-${index}`} className="editor-table-row inhabitant-suggestions-grid">
                <input
                  className="text-input editor-input"
                  value={s.name ?? ''}
                  placeholder="Name"
                  onChange={(e) => updateInhabitantSuggestionField(index, 'name', e.target.value)}
                />
                <input
                  className="text-input editor-input"
                  type="number"
                  value={s.level ?? ''}
                  placeholder="Level"
                  onChange={(e) => updateInhabitantSuggestionField(index, 'level', e.target.value)}
                />
                <input
                  className="text-input editor-input"
                  value={s.title ?? ''}
                  placeholder="Title"
                  onChange={(e) => updateInhabitantSuggestionField(index, 'title', e.target.value)}
                />
                <select
                  className="select-input"
                  value={s.beingType ?? ''}
                  onChange={(e) => updateInhabitantSuggestionField(index, 'beingType', e.target.value)}
                >
                  <option value="">Select...</option>
                  {beingTypeOptions.map((opt) => (
                    <option key={`inh-being-type-${index}-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                  {s.beingType && !beingTypeOptions.some((opt) => opt.value === s.beingType) && (
                    <option value={s.beingType}>{s.beingType}</option>
                  )}
                </select>
                <textarea
                  className="text-area grid-textarea"
                  value={s.description ?? [s.role, s.characterDescription].filter(Boolean).join(': ')}
                  placeholder="Description"
                  onChange={(e) => updateInhabitantSuggestionField(index, 'description', e.target.value)}
                />
                <button
                  type="button"
                  className="icon-button"
                  aria-label="Create being from inhabitant suggestion"
                  title={!place.idEntity ? 'Save this place first' : 'Create and save this being'}
                  disabled={!place.idEntity || generatingInhabitantIndex !== null || isGeneratingInhabitantSuggestions || isGeneratingAllInhabitants || !onGenerateInhabitant}
                  onClick={() => {
                    if (!onGenerateInhabitant) {
                      return;
                    }

                    setGeneratingInhabitantIndex(index);
                    onGenerateInhabitant(s, index, () => setGeneratingInhabitantIndex(null));
                  }}
                >
                  {generatingInhabitantIndex === index ? (
                    <span style={{ fontSize: '10px', padding: '0 2px' }}>...</span>
                  ) : (
                    <SparklesIcon />
                  )}
                </button>
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove inhabitant suggestion"
                  onClick={() => removeInhabitantSuggestion(index)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {inhabitantSuggestions.length === 0 && (
              <div className="empty-grid">No inhabitant suggestions.</div>
            )}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Child Place Suggestions</h3>
            <div className="button-row compact">
              {onGenerateAllSuggestions && (
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  onClick={() => {
                    setIsGeneratingAll(true);
                    onGenerateAllSuggestions(
                      childPlaceSuggestions,
                      () => setIsGeneratingAll(false),
                      childSuggestionPrompt.trim()
                    );
                  }}
                  disabled={!place.idEntity || isGeneratingAll || generatingIndex !== null || childPlaceSuggestions.length === 0}
                  title={!place.idEntity ? 'Save this place first' : 'Create all current child place suggestions'}
                >
                  <SparklesIcon />
                  <span>{isGeneratingAll ? 'Creating...' : 'Create All'}</span>
                </button>
              )}
              {onGenerateSuggestions && (
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  onClick={() => onGenerateSuggestions(childSuggestionPrompt.trim())}
                  disabled={!place.idEntity || isGeneratingSuggestions || isGeneratingAll}
                  title={!place.idEntity ? 'Save this place first' : 'Refresh the child place suggestion list'}
                >
                  <SparklesIcon />
                  <span>{isGeneratingSuggestions ? 'Refreshing...' : 'Refresh Suggestions'}</span>
                </button>
              )}
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={addSuggestion}
                disabled={isGeneratingAll}
              >
                <span>Add Suggestion</span>
              </button>
            </div>
          </div>
          {!place.idEntity && onGenerateSuggestions && (
            <div className="panel-copy" style={{ marginBottom: '8px', color: '#888' }}>
              Save this place first to generate suggestions using related places.
            </div>
          )}
          {!place.idEntity && onGenerateChild && (
            <div className="panel-copy" style={{ marginBottom: '8px', color: '#888' }}>
              Save this place first to enable creating child places.
            </div>
          )}
          {onGenerateSuggestions && (
            <label htmlFor={childSuggestionPromptId} className="editor-field editor-field-spaced">
              <span className="field-label">Suggestion Prompt</span>
              <input
                id={childSuggestionPromptId}
                className="text-input"
                value={childSuggestionPrompt}
                placeholder="give me three rivers"
                onChange={(event) => setChildSuggestionPrompt(event.target.value)}
                disabled={!place.idEntity || isGeneratingSuggestions || isGeneratingAll}
              />
            </label>
          )}
          <div className="editor-table">
            <div className="editor-table-row editor-table-head suggestions-grid-with-action">
              <span>Name</span>
              <span>Place Type</span>
              <span>Zone Type</span>
              <span>Building Type</span>
              <span>Transit Type</span>
              <span>Description</span>
              <span className="table-action-head" title="Create"><SparklesIcon /></span>
              <span className="table-action-head" title="Remove"><TrashIcon /></span>
            </div>
            {childPlaceSuggestions.map((s, index) => (
              <div key={`suggestion-${index}`} className="editor-table-row suggestions-grid-with-action">
                <input
                  className="text-input editor-input"
                  value={s.name ?? ''}
                  onChange={(e) => updateSuggestionField(index, 'name', e.target.value)}
                />
                <select
                  className="select-input"
                  value={s.placeType ?? ''}
                  onChange={(e) => updateSuggestionField(index, 'placeType', e.target.value)}
                >
                  <option value="">Select...</option>
                  {placeTypeOptions.map((opt) => (
                    <option key={`sug-type-${index}-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  className="select-input"
                  value={s.zoneType ?? ''}
                  onChange={(e) => updateSuggestionField(index, 'zoneType', e.target.value)}
                >
                  <option value="">Select...</option>
                  {zoneTypeOptions.map((opt) => (
                    <option key={`sug-zone-${index}-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  className="select-input"
                  value={s.buildingType ?? ''}
                  onChange={(e) => updateSuggestionField(index, 'buildingType', e.target.value)}
                >
                  <option value="">Select...</option>
                  {buildingTypeOptions.map((opt) => (
                    <option key={`sug-building-${index}-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select
                  className="select-input"
                  value={s.transitPointType ?? ''}
                  onChange={(e) => updateSuggestionField(index, 'transitPointType', e.target.value)}
                  disabled={String(s.placeType ?? '').trim().toLowerCase() !== 'transit point'}
                >
                  <option value="">Select...</option>
                  {transitPointTypeOptions.map((opt) => (
                    <option key={`sug-transit-${index}-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <textarea
                  className="text-area grid-textarea"
                  value={s.description ?? ''}
                  onChange={(e) => updateSuggestionField(index, 'description', e.target.value)}
                />
                <button
                  type="button"
                  className="icon-button"
                  aria-label="Create child place"
                  title={!place.idEntity ? 'Save parent first' : `Create ${s.name}`}
                  disabled={!place.idEntity || generatingIndex !== null || isGeneratingAll}
                  onClick={() => {
                    if (!onGenerateChild) return;
                    setGeneratingIndex(index);
                    onGenerateChild(s, () => setGeneratingIndex(null), childSuggestionPrompt.trim());
                  }}
                >
                  {generatingIndex === index ? (
                    <span style={{ fontSize: '10px', padding: '0 2px' }}>...</span>
                  ) : (
                    <SparklesIcon />
                  )}
                </button>
                <button
                  type="button"
                  className="icon-button danger-button"
                  aria-label="Remove suggestion"
                  disabled={isGeneratingAll}
                  onClick={() => removeSuggestion(index)}
                >
                  <TrashIcon />
                </button>
              </div>
            ))}
            {childPlaceSuggestions.length === 0 && (
              <div className="empty-grid">No child place suggestions.</div>
            )}
          </div>
        </section>

        <section className="editor-section">
          <div className="section-header">
            <h3>Top-Down Overview</h3>
            <div className="button-row compact">
              <label className="overview-toggle-control">
                <input
                  type="checkbox"
                  checked={showNestedOverviewChildren}
                  onChange={toggleNestedOverviewChildren}
                  disabled={!overviewPlan || directOverviewChildIds.length === 0 || Boolean(overviewDrag)}
                />
                <span>
                  {isLoadingNestedOverviewChildren ? 'Loading next level...' : 'Show next level'}
                </span>
              </label>
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={() =>
                  changeOverviewZoom((current) => current / PLACE_OVERVIEW_ZOOM_STEP)
                }
                disabled={!overviewPlan || overviewZoom <= PLACE_OVERVIEW_MIN_ZOOM || Boolean(overviewDrag)}
                title="Zoom out"
              >
                <span>Zoom Out</span>
              </button>
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={() =>
                  changeOverviewZoom((current) => current * PLACE_OVERVIEW_ZOOM_STEP)
                }
                disabled={!overviewPlan || Boolean(overviewDrag)}
                title="Zoom in"
              >
                <span>Zoom In</span>
              </button>
              <button
                type="button"
                className="secondary-button button-with-icon"
                onClick={undoOverviewEdit}
                disabled={overviewUndoStack.length === 0 || Boolean(overviewDrag)}
                title="Undo the last overview geometry edit"
              >
                <span>Undo</span>
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => { setOverviewLockedBounds(null); setOverviewZoom(1); }}
                disabled={!overviewPlan || Boolean(overviewDrag)}
                title="Fit the map to the current places and surrounding features"
              >
                Fit Map
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={deleteSelectedOverviewVertex}
                disabled={!selectedOverviewVertex || Boolean(overviewDrag)}
                title="Remove the selected point; paths retain at least two points"
              >
                Delete Point
              </button>
              {onDuplicateChildPlace && (
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  onClick={openDuplicateOverviewModal}
                  disabled={!canDuplicateSelectedOverviewPlace || isDuplicatingOverviewPlace}
                  title={
                    canDuplicateSelectedOverviewPlace
                      ? 'Duplicate selected child place'
                      : 'Select a direct child place to duplicate'
                  }
                >
                  <span>{isDuplicatingOverviewPlace ? 'Duplicating...' : 'Duplicate'}</span>
                </button>
              )}
              <button type="button" className="secondary-button" onClick={() => setNaturalPathTarget(selectedOverviewPlace)}
                disabled={!selectedOverviewPlace || normalizeShapeTypeName(selectedOverviewPlace.shape?.shapeType) !== 'path' ||
                  Boolean(overviewDrag) || !(isSameEntityId(selectedOverviewPlace.idEntity, place.idEntity) ||
                    directOverviewChildIds.some(id => isSameEntityId(id, selectedOverviewPlace.idEntity)))}>
                Naturalize path…
              </button>
              {onRegeneratePath && (
                <button
                  type="button"
                  className="secondary-button button-with-icon"
                  onClick={regenerateSelectedOverviewPath}
                  disabled={!canRegenerateSelectedOverviewPath || isRegeneratingOverviewPath}
                  title={
                    canRegenerateSelectedOverviewPath
                      ? 'Generate a terrain-aware path for the selected road'
                      : 'Select a saved road Path to regenerate'
                  }
                >
                  <SparklesIcon />
                  <span>{isRegeneratingOverviewPath ? 'Regenerating...' : 'Regenerate Path'}</span>
                </button>
              )}
              <span className="existing-list-title">
                Zoom {overviewZoom.toFixed(2)}x
              </span>
            </div>
          </div>
          {naturalPathTarget && <NaturalPathEditor key={naturalPathTarget.idEntity || 'draft'} place={naturalPathTarget}
            onPreview={setNaturalPathPreview}
            onCancel={() => setNaturalPathTarget(null)} onApply={nextPlace => {
              const currentTarget = isSameEntityId(naturalPathTarget.idEntity, place.idEntity) ? place :
                (place.composedOfChildren || []).find(child => isSameEntityId(child.idEntity, naturalPathTarget.idEntity));
              if (!currentTarget || JSON.stringify(currentTarget.shape) !== JSON.stringify(naturalPathTarget.shape)) {
                window.alert('The path changed while previewing. Reopen Naturalize path to use the latest geometry.');
                setNaturalPathTarget(null); return;
              }
              const updated = { ...currentTarget, shape: nextPlace.shape, location: nextPlace.location };
              setOverviewUndoStack(current => [...current, { draftPlace: currentTarget, draftOnly: true }].slice(-25));
              onChange(isSameEntityId(updated.idEntity, place.idEntity) ? updated : { ...place,
                composedOfChildren: (place.composedOfChildren || []).map(child =>
                  isSameEntityId(child.idEntity, updated.idEntity) ? updated : child) });
              syncLoadedOverviewDescendantCache(updated);
              setSelectedOverviewVertex(null);
              setNaturalPathTarget(null);
            }} />}
          <div className="explore-image-frame place-overview-frame" ref={overviewFrameRef}>
            {overviewPlan ? (
              <svg
                className="explore-plan-svg"
                style={overviewSvgStyle}
                viewBox={`${overviewViewBox.x} ${overviewViewBox.y} ${overviewViewBox.width} ${overviewViewBox.height}`}
                role="img"
                aria-label="Top-down overview map of the selected place and visible child places."
                onPointerMove={updateOverviewDrag}
                onPointerUp={finishOverviewDrag}
                onPointerCancel={finishOverviewDrag}
                onDoubleClick={insertNearestOverviewVertex}
              >
                <rect
                  x={overviewViewBox.x}
                  y={overviewViewBox.y}
                  width={overviewViewBox.width}
                  height={overviewViewBox.height}
                  fill="#f8fbff"
                />
                {overviewPlan.nodes.map((node) => (
                  normalizeShapeTypeName(node.shapeType) === 'polygon' && node.points ? (
                    <polygon
                      key={node.id}
                      points={node.points}
                      fill={node.fill}
                      stroke={node.stroke}
                      strokeWidth={getOverviewShapeStrokeWidth(node)}
                      className={`explore-plan-shape ${
                        (selectedOverviewPlaceId || place.idEntity) === node.id ? 'explore-plan-shape-active' : ''
                      }`}
                      style={{ cursor: canMoveOverviewNode(node) ? 'grab' : 'pointer', strokeWidth: getOverviewShapeStrokeWidth(node) }}
                      onPointerDown={(event) => startOverviewDrag(event, node)}
                      onClick={() => selectOverviewPlace(node)}
                    />
                  ) : node.pathData ? (
                    <path key={node.id} d={node.pathData} fill="none" stroke={node.stroke}
                      strokeWidth={getOverviewPathStrokeWidth(node)} className="explore-plan-shape"
                      style={{ cursor: canMoveOverviewNode(node) ? 'grab' : 'pointer' }}
                      onPointerDown={(event) => startOverviewDrag(event, node)} onClick={() => selectOverviewPlace(node)} />
                  ) : normalizeShapeTypeName(node.shapeType) === 'path' && node.points ? (
                    <polyline
                      key={node.id}
                      points={node.points}
                      fill="none"
                      stroke={node.stroke}
                      strokeWidth={getOverviewPathStrokeWidth(node)}
                      className={`explore-plan-shape ${
                        (selectedOverviewPlaceId || place.idEntity) === node.id ? 'explore-plan-shape-active' : ''
                      }`}
                      style={{ cursor: canMoveOverviewNode(node) ? 'grab' : 'pointer', strokeWidth: getOverviewPathStrokeWidth(node) }}
                      onPointerDown={(event) => startOverviewDrag(event, node)}
                      onClick={() => selectOverviewPlace(node)}
                    />
                  ) : ['sphere', 'cylinder'].includes(normalizeShapeTypeName(node.shapeType)) ? (
                    <circle
                      key={node.id}
                      cx={node.centerX}
                      cy={node.centerY}
                      r={node.radius}
                      fill={node.fill}
                      stroke={node.stroke}
                      strokeWidth={getOverviewShapeStrokeWidth(node)}
                      className={`explore-plan-shape ${
                        (selectedOverviewPlaceId || place.idEntity) === node.id ? 'explore-plan-shape-active' : ''
                      }`}
                      style={{ cursor: canMoveOverviewNode(node) ? 'grab' : 'pointer', strokeWidth: getOverviewShapeStrokeWidth(node) }}
                      onPointerDown={(event) => startOverviewDrag(event, node)}
                      onClick={() => selectOverviewPlace(node)}
                    />
                  ) : (
                    <rect
                      key={node.id}
                      x={node.x}
                      y={node.y}
                      width={node.width}
                      height={node.height}
                      fill={node.fill}
                      stroke={node.stroke}
                      strokeWidth={getOverviewShapeStrokeWidth(node)}
                      className={`explore-plan-shape ${
                        (selectedOverviewPlaceId || place.idEntity) === node.id ? 'explore-plan-shape-active' : ''
                      }`}
                      style={{ cursor: canMoveOverviewNode(node) ? 'grab' : 'pointer', strokeWidth: getOverviewShapeStrokeWidth(node) }}
                      onPointerDown={(event) => startOverviewDrag(event, node)}
                      onClick={() => selectOverviewPlace(node)}
                    />
                  )
                ))}
                {naturalPathTarget && naturalPathPreview && <polyline
                  points={naturalPathPreview.map(p => `${overviewPlan.toCanvasX(p.x)},${overviewPlan.toCanvasY(p.z)}`).join(' ')}
                  fill="none" stroke="#1677bd" strokeWidth="3" strokeDasharray="6 3" pointerEvents="none">
                  <title>Naturalized path preview</title>
                </polyline>}
                {overviewPlan.nodes.filter(node => normalizeShapeTypeName(node.shapeType) !== 'path'
                  && Math.max(node.width, node.height) * overviewHandleScale < 4).map(node => (
                    <circle key={`${node.id}-marker`} className="place-overview-site-marker"
                      cx={node.centerX} cy={node.centerY} r={3 / overviewHandleScale} fill={node.stroke}
                      style={{ cursor: canMoveOverviewNode(node) ? 'grab' : 'pointer' }}
                      onPointerDown={event => startOverviewDrag(event, node)} onClick={() => selectOverviewPlace(node)}>
                      <title>{node.label}</title>
                    </circle>
                  ))}
                {overviewPlan.nodes
                  .filter((node) => node.renderLayer > 0 && (node.points || node.pathData))
                  .map((node) => {
                    const shapeType = normalizeShapeTypeName(node.shapeType);
                    const overlayStrokeWidth =
                      shapeType === 'path'
                        ? getOverviewPathStrokeWidth(node)
                        : getOverviewShapeStrokeWidth(node);
                    const overlayKey = `${node.id}-linear-overlay`;
                    const commonOverlayProps = {
                      points: node.points,
                      fill: 'none',
                      stroke: node.stroke,
                      strokeWidth: overlayStrokeWidth,
                      strokeLinecap: 'round',
                      strokeLinejoin: 'round',
                      pointerEvents: 'none',
                      style: { strokeWidth: overlayStrokeWidth },
                    };

                    if (node.pathData) return <path key={overlayKey} d={node.pathData} {...commonOverlayProps} />;
                    if (shapeType === 'path') {
                      return <polyline key={overlayKey} {...commonOverlayProps} />;
                    }

                    if (shapeType === 'polygon') {
                      return <polygon key={overlayKey} {...commonOverlayProps} />;
                    }

                    return null;
                  })}
                {overviewPlan.nodes.flatMap((node) =>
                  canEditOverviewNode(node)
                    ? node.edgeSegments.map((edge) => {
                        const canDragEdge = !isRectangularOverviewShape(normalizeShapeTypeName(node.shapeType));

                        return (
                          <line
                            key={edge.key}
                            aria-label={`${node.label}${Number.isInteger(edge.pathIndex) ? ` path ${edge.pathIndex + 1}` : ''} edge ${edge.fromIndex + 1}`}
                            x1={edge.x1}
                            y1={edge.y1}
                            x2={edge.x2}
                            y2={edge.y2}
                            stroke="rgba(0, 0, 0, 0.001)"
                            strokeWidth={overviewEdgeHitStrokeWidth}
                            className="explore-plan-shape"
                            style={{
                              cursor: canDragEdge ? 'move' : 'copy',
                              pointerEvents: 'all',
                              strokeWidth: overviewEdgeHitStrokeWidth,
                            }}
                            onPointerDown={(event) => {
                              if (event.altKey && canMoveOverviewNode(node)) {
                                startOverviewDrag(event, node);
                              } else if (canDragEdge) {
                                startOverviewEdgeDrag(event, node, edge);
                              }
                            }}
                            onDoubleClick={(event) => insertOverviewVertex(event, node, edge)}
                          />
                        );
                      })
                    : []
                )}
                {overviewPlan.nodes.flatMap((node) =>
                  canEditOverviewNode(node)
                    ? node.vertexPoints.map((vertex) => {
                        const isSelectedVertex =
                          selectedOverviewVertex?.id === node.id && selectedOverviewVertex?.index === vertex.index &&
                          selectedOverviewVertex?.pathIndex === vertex.pathIndex;
                        return (
                          <g
                            key={vertex.key}
                            aria-label={`${node.label}${Number.isInteger(vertex.pathIndex) ? ` path ${vertex.pathIndex + 1}` : ''} point ${vertex.index + 1}`}
                            style={{ cursor: 'grab' }}
                            onPointerDown={(event) => startOverviewVertexDrag(event, node, vertex)}
                          >
                            <circle
                              cx={vertex.x}
                              cy={vertex.y}
                              r={isSelectedVertex ? selectedOverviewVertexRadius : overviewVertexRadius}
                              fill="#dc2626"
                              stroke={isSelectedVertex ? '#111827' : '#ffffff'}
                              strokeWidth={isSelectedVertex ? selectedOverviewVertexStrokeWidth : overviewVertexStrokeWidth}
                              pointerEvents="none"
                            />
                            <circle
                              cx={vertex.x}
                              cy={vertex.y}
                              r={overviewVertexHitRadius}
                              fill="transparent"
                              stroke="transparent"
                              pointerEvents="all"
                            />
                          </g>
                        );
                      })
                    : []
                )}
              </svg>
            ) : (
              <div className="explore-image-placeholder">No overview map available.</div>
            )}
          </div>
          <div className="existing-list-title">
            {selectedOverviewPlace
              ? `Selected: ${getPlaceLabel(selectedOverviewPlace)}`
              : 'Click a shape to display the place name.'}
          </div>
          <p className="existing-list-title">
            Drag red points or edges to reshape. Double-click an edge to add a point.
            Select a point and use Delete Point or Delete to remove it. Hold Alt while dragging to move a whole place.
            Separate path sections stay separate; drag endpoints together to close an opening. Undo restores the last edit.
          </p>
        </section>

      </div>
      {isDuplicateOverviewModalOpen && (
        <div className="modal-overlay">
          <form className="modal-panel" onSubmit={submitDuplicateOverviewPlace}>
            <h3>Duplicate Place</h3>
            <label htmlFor="duplicate-overview-place-name" className="editor-field">
              <span className="field-label">Name</span>
              <input
                id="duplicate-overview-place-name"
                className="text-input editor-input"
                value={duplicateOverviewName}
                onChange={(event) => setDuplicateOverviewName(event.target.value)}
                autoFocus
                disabled={isDuplicatingOverviewPlace}
              />
            </label>
            <div className="button-row compact">
              <button
                type="button"
                className="secondary-button"
                onClick={closeDuplicateOverviewModal}
                disabled={isDuplicatingOverviewPlace}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button-with-icon"
                disabled={!duplicateOverviewName.trim() || isDuplicatingOverviewPlace}
              >
                <span>{isDuplicatingOverviewPlace ? 'Duplicating...' : 'Duplicate'}</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default PlaceEditor;
